/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useParams } from "react-router-dom"
import { toast } from "sonner"

import type {
  ActivityMessage,
  Artifact,
  ChatAppRequest,
  ChatAppRequestOverrides,
  ChatSessionModel,
  DeepAskUserMessage,
  DeepCitationVerificationMessage,
  DeepDeliverableTypeMessage,
  DeepFollowupSuggestionsMessage,
  DeepOrchestratorReasoningChunkMessage,
  DeepOrchestratorReasoningCompleteMessage,
  DeepOrchestratorReasoningDiscardMessage,
  DeepPlanningChunkMessage,
  DeepPlanProposedMessage,
  DeepStateSnapshotMessage,
  DeepSubagentChunkMessage,
  DeepSubagentReasoningChunkMessage,
  DeepSubagentReasoningCompleteMessage,
  DeepSubagentReasoningDiscardMessage,
  DeepToolApprovalRequiredMessage,
  DurableChatTurnMode,
  GraphFileItem,
  ResponseMessage,
  ScholarState,
  ServerMessage,
  UserDocument,
} from "@/api/models"
import type { StoreApi, UseBoundStore } from "zustand"

import {
  explorerChatWebSocketApi,
  explorerDeepChatWebSocketApi,
  getDeepStateApi,
  removeSessionFileApi,
  scholarChatWebSocketApi,
  startResearchApi,
} from "@/api/api"
import { GPT4VInput, VectorFieldOptions } from "@/api/models"
import { Cookies } from "@/constants/cookies"
import { useRoutes } from "@/contexts/use-routes"
import { useM365ApprovalGate } from "@/features/ai-explorer/hooks/use-m365-approval-gate"
import {
  translatorOverridesForSource,
  type TranslatorRequest,
} from "@/features/ai-translator/translator-tool-utils"
import { useCanvasStore } from "@/features/canvas/stores/canvas-store"
import {
  adoptChatRequestGeneration,
  chatRequestOwnsSession,
  getChatRequestControl,
  getChatRequestGenerationId,
  getChatRequestSessionDisposalGeneration,
  isChatRequestGenerationCurrent,
  isChatRequestSessionDisposalGenerationCurrent,
  isChatRequestSessionDisposed,
} from "@/features/chat/hooks/chat-request-registry"
import { useChat } from "@/features/chat/hooks/use-chat"
import { useChatScroll } from "@/features/chat/hooks/use-chat-scroll"
import { useHighReasoning } from "@/features/chat/hooks/use-high-reasoning"
import { useStartNewChat } from "@/features/chat/hooks/use-start-new-chat"
import { useWebSocketMessageHandler } from "@/features/chat/hooks/use-websocket-message-handler"
import { isSystemNoise } from "@/features/chat/utils/activity-message-utils"
import {
  resolveReasoningEffortForRequest,
  resolveRequestedReasoningEffort,
} from "@/features/chat/utils/chat-request-utils"
import { getDurableChatTurnMode } from "@/features/chat/utils/durable-chat-turn-mode"
import { graphDownloadErrorMessage } from "@/features/files/graph-download-error-message"
import { buildPluginRequestOverrides } from "@/features/plugins/plugin-request"
import { useCurrentSessionProject } from "@/features/projects/hooks/use-current-session-project"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { useFeatureEnabled } from "@/hooks/use-feature-enabled"
import { useGPTModelSettings } from "@/hooks/use-gpt-model-settings"
import { useAIExplorerSettings } from "@/hooks/use-module-settings"
import { useProject } from "@/hooks/use-projects"
import {
  useDeepExplorerEnabled,
  useM365Enabled,
  useNetboxEnabled,
  usePluginsEnabled,
  useRouteModelDefaults,
  useServiceDeskEnabled,
  useWebSearchEnabled,
  useXOneEnabled,
} from "@/hooks/use-route-overrides"
import { useSessionId } from "@/hooks/use-session-id"
import i18n from "@/languageConfig"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { ChatService } from "@/services/chat-service"
import { cookieService } from "@/services/cookie-service"
import { SettingsService } from "@/services/settings-service"
import {
  type DeepActivityEvent,
  type DeepExplorerSessionState,
  type DeepFile,
  getDeepStateForSession,
  getLatestPlanArtifact,
  useAIExplorerStore,
} from "@/stores/ai-explorer-store"
import { useAISettingsStore } from "@/stores/ai-settings-store"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"
import { useNavigatorStore } from "@/stores/navigator-store"
import { usePreferencesStore } from "@/stores/preferences-store"
import {
  type ResearchPreferences,
  sanitizeResearchDepth,
  useResearchPreferencesStore,
} from "@/stores/research-preferences-store"
import { defaultSessionState } from "@/stores/slices/chat-slice"
import { getExampleQuestions } from "@/utils/config-utils"
import {
  getAssistantFiles,
  getResponseContent,
  getSessionState,
} from "@/utils/response-format-utils"

import { AGENT_RUNS_QUERY_KEY } from "./use-agent-runs-query"
import { useIndexedFilesQuery } from "./use-indexed-files-query"
import { useRemoveIndexedFileMutation } from "./use-remove-indexed-file-mutation"
import { useSessionFileRefs } from "./use-session-file-refs"
import { parseFollowupsFromFile } from "../components/SmartFollowupCards-utils"
import { shouldOpenSessionInDeepMode } from "../utils/deep-session-mode"
import {
  cancelDeepToolApprovalsForSession,
  getDeepSocketSender,
  registerDeepSocketSender,
  registerDeepToolApproval,
  unregisterDeepSocketSender,
} from "../utils/deep-tool-approvals"
import { mergeDeepTodosForSnapshot } from "../utils/deepTodoState"
import {
  deriveSourcesFromActivity,
  deriveSourcesFromEvent,
} from "../utils/deriveSourcesFromActivity"
import {
  isDeepPlanQueuePrefix,
  isFreshSettle,
  isNewlyEligibleToDrain,
  shouldAttemptDrain,
  shouldDrainQueue,
} from "../utils/message-queue-drain"
import { resetDeepPlanApprovalForTurn } from "../utils/reset-deep-plan-approval"
import { synthesizeDocumentFromDeliverable } from "../utils/synthesizeDocumentFromDeliverable"

function processCountryCodes(countryCodes: string): string[] {
  return countryCodes
    .split(",")
    .map((c) => c.trim())
    .filter((c) => c)
}

function processSourceUrls(sourceUrls: string): string[] {
  return sourceUrls
    .split(/\n|,/)
    .map((u) => u.trim())
    .filter((u) => u)
}

function buildAgentOverrides(
  researchPrefs: ResearchPreferences,
  reportFormatPayload: string | { filename: string; content: number[] } | undefined,
  reportFormatSourcePayload: "file" | "message" | "standard" | "structured_summary"
) {
  const normalizedDepth = sanitizeResearchDepth(researchPrefs.researchDepth)

  const agentOverrides = {
    report_length: researchPrefs.reportLength,
    research_depth: normalizedDepth,
    report_language: researchPrefs.language,
    report_format: reportFormatPayload,
    report_format_source: reportFormatSourcePayload,
    sources: researchPrefs.sources,
    country_codes: processCountryCodes(researchPrefs.countryCodes),
    source_urls: processSourceUrls(researchPrefs.sourceUrls),
    trusted_mode: researchPrefs.trustedMode,
    file_handling_strategy: researchPrefs.fileHandlingStrategy,
  }

  if (researchPrefs.reportLength === "custom") {
    return {
      ...agentOverrides,
      custom_report_length: researchPrefs.customReportLength,
    }
  }

  return agentOverrides
}

function legacyTimestampString(value: unknown): string {
  if (typeof value === "number") return new Date(value).toISOString()
  if (typeof value === "string") return value
  return ""
}

function restoreFileInOriginalOrder(
  current: string[],
  fileName: string,
  originalOrder: string[]
): string[] {
  if (current.includes(fileName)) return current

  const originalIndex = originalOrder.indexOf(fileName)
  if (originalIndex === -1) return [...current, fileName]

  for (let index = originalIndex + 1; index < originalOrder.length; index += 1) {
    const nextIndex = current.indexOf(originalOrder[index])
    if (nextIndex !== -1) {
      return [...current.slice(0, nextIndex), fileName, ...current.slice(nextIndex)]
    }
  }

  for (let index = originalIndex - 1; index >= 0; index -= 1) {
    const previousIndex = current.indexOf(originalOrder[index])
    if (previousIndex !== -1) {
      return [...current.slice(0, previousIndex + 1), fileName, ...current.slice(previousIndex + 1)]
    }
  }

  return [...current, fileName]
}

type SessionFileRemovalBatch = {
  pending: Set<string>
  cacheOrder: string[]
  filesInIndexOrder: string[]
  sessionFileRefsOrder: string[]
  hasSuccessfulRemoval: boolean
}

type AIExplorerChatOptions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useStore?: (sessionType: string) => UseBoundStore<StoreApi<any>>
  agentName?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getExtraOverrides?: (store: UseBoundStore<StoreApi<any>>) => Record<string, unknown>
}

/** The backend stamps every frame of one request with `_request_generation_id`. */
function requestGenerationOf(message: unknown): string | null {
  const value = (message as { _request_generation_id?: unknown } | null)?._request_generation_id
  return typeof value === "string" && value.length > 0 ? value : null
}

export function useAIExplorerChat(options?: AIExplorerChatOptions) {
  const { routes } = useRoutes()
  const queryClient = useQueryClient()

  // Determine session type from current route
  const sessionType = useCurrentSessionType()

  // Most modules use their route id as the session type, but a route may
  // declare a distinct `overrideSessionType` (HERMES: the "agents" route maps
  // to the "hosted-agent" session type). Match either so hosted-agent chats
  // resolve to their owning route instead of throwing.
  const moduleRoute = routes.find(
    (route) => route.id === sessionType || route.overrides?.overrideSessionType === sessionType
  )
  if (!moduleRoute) {
    throw new Error(`Module route not found for sessionType: ${sessionType}`)
  }

  const moduleRouteId = moduleRoute.id
  const overrides = moduleRoute.overrides
  // Session-scoped lookups (the open-session store keyed by session type) must
  // use the resolved session type, which may differ from the route id when a
  // route declares `overrideSessionType` (HERMES: "agents" route → "hosted-agent"
  // sessions). `route_id` on the wire keeps using `moduleRouteId`. Identical to
  // the route id for every module that doesn't override, so a no-op there.
  const moduleSessionType = overrides?.overrideSessionType ?? moduleRouteId
  const isWebSearchAvailable = useWebSearchEnabled()
  const isM365Available = useM365Enabled()
  const isServiceDeskAvailable = useServiceDeskEnabled()
  const isNetboxAvailable = useNetboxEnabled()
  const isXOneAvailable = useXOneEnabled()
  const isPluginsAvailable = usePluginsEnabled()
  const { projectId } = useParams()
  // Feeds the project-default-model fallback in useHighReasoning below — see
  // the comment on effectiveModel there for why this replaces trusting the
  // raw persisted gptModel setting as the generic "no override" fallback.
  // Resolved independently of `projectId` above (which stays URL-only for its
  // existing WS/session-key uses): project chats reached via /chat/:sessionId
  // carry no projectId in the URL, so this also falls back to the session's
  // own project association — matching how ai-explorer-page.tsx resolves
  // activeProject for display, so both sides agree.
  const sessionProjectIdForModelDefault = useCurrentSessionProject()
  const { data: activeProject } = useProject(
    projectId ?? sessionProjectIdForModelDefault ?? undefined,
    sessionType
  )

  const defaultSearchServiceId =
    typeof moduleRoute.overrides?.["search_service"] === "string"
      ? moduleRoute.overrides["search_service"]
      : sessionType

  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null)
  const [settingsService] = useState<SettingsService>(SettingsService.getInstance())
  const [chatService] = useState<ChatService>(ChatService.getInstance())

  // Get namespaced stores for this sessionType
  const useStore = options?.useStore ?? useAIExplorerStore
  const explorerStore = useStore(sessionType) as ReturnType<typeof useAIExplorerStore>
  const navigatorStore = useNavigatorStore(sessionType)
  const researchPrefsStore = useResearchPreferencesStore(sessionType)

  // Get variant settings for category filtering
  const { settings: variantSettings } = useAIExplorerSettings(sessionType)

  const enableWebSearch = explorerStore((state) => state.enableWebSearch)
  const setEnableWebSearch = explorerStore((state) => state.setEnableWebSearch)
  const enableAgent = explorerStore((state) => state.enableAgent)
  const enableDeep = explorerStore((state) => state.enableDeep)
  const setEnableDeep = explorerStore((state) => state.setEnableDeep)
  const isDeepExplorerEnabled = useDeepExplorerEnabled()
  // Deep mode follows the chat (#3060). A chat opened from history parks its id
  // here until its saved messages load, then takes the mode of its latest
  // answer. A hand-made Deep choice clears the entry so the restore can't
  // overrule it.
  const deepModeRestorePendingSessionRef = useRef<string | null>(null)
  // Session whose Deep run the state endpoint reported as still in progress.
  const liveDeepRunSessionRef = useRef<string | null>(null)
  // Chat the user explicitly asked to open in Deep mode (the runs panel opening
  // a run's workspace). The choice is made before navigating, so it is keyed to
  // the target chat: it survives that session switch and outranks the chat's
  // latest-answer restore.
  const forcedDeepSessionRef = useRef<string | null>(null)
  // Mode each chat's latest turn from this window was sent in, keyed to that
  // request's generation. Sending the first turn of a new chat navigates to it,
  // and the router's late catch-up re-selects the chat as a history load
  // (#3347). While that turn is still live, it decides the chat's mode.
  const sentTurnModeRef = useRef(new Map<string, { deep: boolean; requestGenerationId: string }>())
  const lastTranslationBySessionRef = useRef(new Map<string, TranslatorRequest | null>())
  const pendingTranslationByRequestRef = useRef(
    new WeakMap<ChatAppRequest, TranslatorRequest | null>()
  )

  function translationForRequest(sessionId: string, isRetry: boolean) {
    const selected = explorerStore.getState().translationRequest
    const snapshot =
      selected ?? (isRetry ? (lastTranslationBySessionRef.current.get(sessionId) ?? null) : null)
    lastTranslationBySessionRef.current.set(sessionId, snapshot)
    return snapshot
  }

  function onDurableChatTurnAccepted(request: ChatAppRequest) {
    const snapshot = pendingTranslationByRequestRef.current.get(request)
    if (snapshot && explorerStore.getState().translationRequest === snapshot) {
      explorerStore.getState().setTranslationRequest(null)
    }
    pendingTranslationByRequestRef.current.delete(request)
  }

  // Defense in depth for the `explorer_deep` admin feature flag AND the
  // per-route `disableDeep` override. Three other paths in this hook can
  // promote `enableDeep` to true without going through the footer toolbar
  // gate: live-run checkpoint hydration, the history restore (the chat's
  // latest answer was Deep), and the `/deep` slash command. The global
  // Cmd/Ctrl+Shift+D shortcut routes through `handleDeepChange` too. If any
  // of these flip the flag while Deep is unavailable, the WS send routes to
  // `/api/ws/explorer-deep` and the backend 403s — and with the toolbar
  // toggle hidden, the user has no way to turn it back off. Force the flag to
  // false whenever Deep is disabled (feature off OR route-locked) so the
  // upstream state matches what the UI allows.
  const isDeepRouteLocked = overrides?.disableDeep === true
  const { data: config } = useConfigQuery()
  const useResponsesFileSearch = config?.useResponsesFileSearch ?? false
  useEffect(() => {
    // A route-level `disableDeep` comes from settings (the routes config), not
    // `/api/config`, so it's known on first render. Clear immediately — don't
    // wait for config — so a hydration/slash-command path can't leave deep on
    // (and route the next send to the deep WS) during the config-load window.
    if (isDeepRouteLocked && enableDeep) {
      setEnableDeep(false)
      return
    }
    // Feature-flag path: `useFeatureEnabled` returns false during the initial
    // `/api/config` request, so gate on `config` here — clearing `enableDeep`
    // then would clobber session-checkpoint hydration that races the fetch.
    if (!config) return
    if (!isDeepExplorerEnabled && enableDeep) {
      setEnableDeep(false)
    }
  }, [isDeepRouteLocked, config, isDeepExplorerEnabled, enableDeep, setEnableDeep])
  const enableNarration = explorerStore((state) => state.enableNarration)
  const enableFactCheck = explorerStore((state) => state.enableFactCheck)
  const enableTreeRetrieval = explorerStore((state) => state.enableTreeRetrieval)
  const setEnableTreeRetrieval = explorerStore((state) => state.setEnableTreeRetrieval)
  const isTreeRetrievalFeatureEnabled = useFeatureEnabled("tree_retrieval")

  // Defense in depth for the `tree_retrieval` admin feature flag. When the
  // admin disables tree retrieval, force `enableTreeRetrieval` to false so
  // the request payload doesn't ask for a tool the backend has already
  // stripped from the explorer agent. Same `config` gate as above: avoid
  // clobbering session-checkpoint hydration that races the config fetch.
  useEffect(() => {
    if (!config) return
    if (!isTreeRetrievalFeatureEnabled && enableTreeRetrieval) {
      setEnableTreeRetrieval(false)
    }
  }, [config, isTreeRetrievalFeatureEnabled, enableTreeRetrieval, setEnableTreeRetrieval])
  const setEnableAgent = explorerStore((state) => state.setEnableAgent)
  const setReasoningMode = explorerStore((state) => state.setReasoningMode)
  const setModelOverrideStore = explorerStore((state) => state.setModelOverride)
  const setEffortOverrideStore = explorerStore((state) => state.setEffortOverride)
  const replaceDeepState = explorerStore((state) => state.replaceDeepState)
  const setDeepPlanProposed = explorerStore((state) => state.setDeepPlanProposed)
  const setDeepPlanApproved = explorerStore((state) => state.setDeepPlanApproved)
  const setAskUserProposed = explorerStore((state) => state.setAskUserProposed)
  const appendArtifact = explorerStore((state) => state.appendArtifact)
  const applyArtifactPatch = explorerStore((state) => state.applyArtifactPatch)
  const hydrateArtifactsFromContext = explorerStore((state) => state.hydrateArtifactsFromContext)
  const focusArtifactAction = explorerStore((state) => state.focusArtifact)
  const selectedPromptId = explorerStore((state) => state.selectedPromptId)
  const selectedPromptTitle = explorerStore((state) => state.selectedPromptTitle)
  const clearSelectedPrompt = explorerStore((state) => state.clearSelectedPrompt)
  const enableM365 = explorerStore((state) => state.enableM365)
  const setEnableM365 = explorerStore((state) => state.setEnableM365)
  const enableServiceDesk = explorerStore((state) => state.enableServiceDesk)
  const setEnableServiceDesk = explorerStore((state) => state.setEnableServiceDesk)
  const enableNetbox = explorerStore((state) => state.enableNetbox)
  const setEnableNetbox = explorerStore((state) => state.setEnableNetbox)
  const enableXOne = explorerStore((state) => state.enableXOne)
  const setEnableXOne = explorerStore((state) => state.setEnableXOne)
  const enablePlugins = explorerStore((state) => state.enablePlugins)
  const setEnablePlugins = explorerStore((state) => state.setEnablePlugins)
  const disabledPluginIds = explorerStore((state) => state.disabledPluginIds)
  const setDisabledPluginIds = explorerStore((state) => state.setDisabledPluginIds)
  const setPluginDisabled = explorerStore((state) => state.setPluginDisabled)
  const setInstalledPluginIds = explorerStore((state) => state.setInstalledPluginIds)
  const hasInitializedDefaults = explorerStore((state) => state.hasInitializedDefaults)
  const setHasInitializedDefaults = explorerStore((state) => state.setHasInitializedDefaults)
  const [showAgentModeInfo, setShowAgentModeInfo] = useState(false)
  const [showM365Info, setShowM365Info] = useState(false)
  const {
    approveM365,
    declineM365,
    hasM365Approval,
    hasDeclinedM365,
    isM365ApprovalLoading,
    isM365ApprovalUnresolved,
  } = useM365ApprovalGate()
  const openPreferencesWith = usePreferencesStore((s) => s.openWith)
  const [activityMessage, setActivityMessage] = useState<string>(
    i18n.t("pages.common.answer_thinking", { defaultValue: "Thinking..." })
  )
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [usePromptTemplateForNextMessage, setUsePromptTemplateForNextMessage] = useState(false)

  useEffect(() => {
    if (hasInitializedDefaults) return
    if (isM365ApprovalLoading) return
    // Initialize to true only if the feature is available for this route
    setEnableWebSearch(isWebSearchAvailable)
    setEnableM365(isM365Available && hasM365Approval)
    setEnableServiceDesk(isServiceDeskAvailable)
    setEnableNetbox(isNetboxAvailable)
    setEnableXOne(isXOneAvailable)
    setEnablePlugins(true)
    setDisabledPluginIds([])
    setHasInitializedDefaults(true)
  }, [
    hasInitializedDefaults,
    hasM365Approval,
    isM365ApprovalLoading,
    setEnableWebSearch,
    setEnableM365,
    setEnableServiceDesk,
    setEnableNetbox,
    setEnableXOne,
    setEnablePlugins,
    setDisabledPluginIds,
    setHasInitializedDefaults,
    isWebSearchAvailable,
    isM365Available,
    isServiceDeskAvailable,
    isNetboxAvailable,
    isXOneAvailable,
  ])

  const { gptModel, shouldStream, suggestFollowupQuestions } = useGPTModelSettings(sessionType)
  const chatState = explorerStore((state) => {
    const currentId = state.currentSessionId
    return currentId ? state.sessionStates[currentId] || defaultSessionState : defaultSessionState
  })
  const files = explorerStore((state) => state.files)
  const selectedDocuments = explorerStore((state) => state.selectedDocuments)
  const addDocument = explorerStore((state) => state.addDocument)
  const removeDocument = explorerStore((state) => state.removeDocument)
  const resetDocuments = explorerStore((state) => state.resetDocuments)
  const filesInIndex = explorerStore((state) => state.filesInIndex)
  const setFilesInIndex = explorerStore((state) => state.setFilesInIndex)
  const sessionFileRefs = explorerStore((state) => state.sessionFileRefs)
  const setSessionFileRefs = explorerStore((state) => state.setSessionFileRefs)
  const enabledLibraries = navigatorStore((state) => state.enabledLibraries)
  const activeCitations = navigatorStore((state) => state.activeCitations)
  const currentSessionId = useSessionId()
  const isCanvasOpen = useContentSidebarStore(
    (state) => state.isOpen && state.currentPanel?.id === "canvas"
  )
  const activeCanvasDocumentId = useCanvasStore((state) => state.documentId)
  const activeCanvasDocumentCanEdit = useCanvasStore((state) => state.activeDocumentCanEdit)
  const activeCanvasDocumentTitle = useCanvasStore((state) => state.title)
  const activeCanvasDocumentContent = useCanvasStore((state) => state.content)
  const activeCanvasSelectionText = useCanvasStore((state) => state.activeSelectionText)
  const activeCanvasSelectionStart = useCanvasStore((state) => state.activeSelectionStart)
  const activeCanvasSelectionEnd = useCanvasStore((state) => state.activeSelectionEnd)

  // Get settings from store for prompts and other non-DB settings
  const settings = useAISettingsStore((state) => state.settings)

  // Load session file refs from cached sessions query (same cache as sidebar - no extra API call)
  const { data: sessionFileRefsFromQuery = [] } = useSessionFileRefs(
    currentSessionId ?? undefined,
    sessionType,
    projectId
  )
  const pendingSessionFileRemovalsRef = useRef<Map<string, SessionFileRemovalBatch>>(new Map())
  const pendingCurrentSessionFiles = currentSessionId
    ? pendingSessionFileRemovalsRef.current.get(currentSessionId)?.pending
    : undefined
  const visibleSessionFileRefsFromQuery = pendingCurrentSessionFiles
    ? sessionFileRefsFromQuery.filter((ref) => !pendingCurrentSessionFiles.has(ref))
    : sessionFileRefsFromQuery

  // Sync file refs from query to store
  useEffect(() => {
    // Only update if there's a meaningful change
    if (visibleSessionFileRefsFromQuery.length > 0 || sessionFileRefs.length > 0) {
      const refsMatch =
        visibleSessionFileRefsFromQuery.length === sessionFileRefs.length &&
        visibleSessionFileRefsFromQuery.every((ref, i) => ref === sessionFileRefs[i])

      if (!refsMatch) {
        setSessionFileRefs(visibleSessionFileRefsFromQuery)
      }
    }
  }, [visibleSessionFileRefsFromQuery, sessionFileRefs, setSessionFileRefs])

  // Deep Explorer streams pre-approval reasoning into `streamingAnswer.thinking`
  // and post-approval tokens into `streamingAnswer.content`. We expose the sum
  // of their lengths as an explicit version counter so `useChatScroll` re-runs
  // its streaming scroll effect on every append — even if zustand/React ever
  // batch two updates into the same tuple reference. See
  // UseChatScrollProps.streamingVersion for the full rationale.
  const streamingAnswerThinkingLength = chatState.streamingAnswer?.[1]?.thinking?.length ?? 0
  const streamingAnswerContentLength = chatState.streamingAnswer?.[1]?.content?.length ?? 0
  const streamingVersion = streamingAnswerThinkingLength + streamingAnswerContentLength

  useChatScroll({ chatMessageStreamEnd, chatState, streamingVersion, sessionId: currentSessionId })

  const { handleStartNewChat: originalHandleStartNewChat, handleSharedLeaveRedirect } =
    useStartNewChat({
      useChatStore: () => explorerStore,
      sessionType,
    })

  const resetAdvancedSettings = researchPrefsStore((state) => state.resetAdvancedSettings)

  const handleStartNewChat = () => {
    originalHandleStartNewChat()
    resetAdvancedSettings()
    setFilesInIndex([])
    resetDocuments()
    setEnableAgent(false)
    setEnableWebSearch(isWebSearchAvailable)
    setReasoningMode("auto")
    setModelOverrideStore(null)
    setEffortOverrideStore(null)
    setEnableM365(isM365Available && hasM365Approval)
    setEnableServiceDesk(isServiceDeskAvailable)
    setEnableNetbox(isNetboxAvailable)
    setEnableXOne(isXOneAvailable)
    setEnablePlugins(true)
    setDisabledPluginIds([])
    setEnableTreeRetrieval(false)
    // Deep agent reset is handled inside useStartNewChat now — every
    // new-chat entry point (sidebar, logo, wrapper) resets it uniformly.
  }

  // Read early (moved ahead of its original #2437 queue-drain section below)
  // so the undelivered-steer fallback in the WS message handler can gate on
  // it too — the queue only auto-drains when this flag is on.
  const isMessageQueueEnabled = useFeatureEnabled("explorer_message_queue")

  const baseOnMessage = useWebSocketMessageHandler({
    useChatStore: () => explorerStore,
    setActivityMessage,
    setIsLoading,
    setHasError,
    setErrorMessage,
    sessionType,
    isMessageQueueEnabled,
  })

  // ── Deep WS send refs ────────────────────────────────────────────────────
  // Holds the send() function for each active deep-mode WebSocket. A foreground
  // run and a backgrounded run can overlap, so outbound control frames must be
  // routed by session id instead of through one shared "latest socket" slot.
  // The live Deep socket sender per session lives in a module-level registry
  // (deep-tool-approvals.ts), not a ref: a pending tool approval outlives this
  // hook instance across a remount and must still find the reattached socket.
  const getDeepWsSend = useCallback(
    (sessionId?: string | null) => getDeepSocketSender(sessionId),
    []
  )

  // ── Deep state snapshot throttle ─────────────────────────────────────────
  // Throttle deep_state_snapshot dispatch to ~200ms (5 updates/sec max).
  // Keyed by sessionId so two interleaved WS streams (a backgrounded run plus
  // a fresh foreground turn) accumulate into separate buckets — without this,
  // the older single-bucket form mixed snapshots from both runs into one bag.
  const snapshotThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingSnapshotRef = useRef<
    Map<
      string,
      {
        disposalGeneration: number
        requestGenerationId: string
        delta: Partial<DeepExplorerSessionState>
      }
    >
  >(new Map())
  // Last live focus observed when the newest buffered autofocus decision was
  // made. A later live mismatch therefore means the user selected a file
  // after that decision; programmatic focus changes update this value too.
  const pendingSnapshotActiveFileBaselineRef = useRef<Map<string, string | null>>(new Map())

  const flushPendingSnapshot = useCallback(() => {
    const pending = pendingSnapshotRef.current
    for (const [sid, { disposalGeneration, requestGenerationId, delta }] of pending) {
      if (
        isChatRequestSessionDisposed(explorerStore, sid) ||
        !isChatRequestSessionDisposalGenerationCurrent(explorerStore, sid, disposalGeneration) ||
        !isChatRequestGenerationCurrent(explorerStore, sid, requestGenerationId)
      )
        continue
      const hasActiveFileBaseline = pendingSnapshotActiveFileBaselineRef.current.has(sid)
      const activeFileBaseline = pendingSnapshotActiveFileBaselineRef.current.get(sid)
      const liveActiveFile = getDeepStateForSession(explorerStore.getState(), sid).activeFile
      const liveActiveFileStillExists =
        liveActiveFile === null ||
        delta.files === undefined ||
        delta.files.some((file) => file.path === liveActiveFile)
      const resolvedDelta =
        hasActiveFileBaseline && liveActiveFile !== activeFileBaseline && liveActiveFileStillExists
          ? { ...delta, activeFile: liveActiveFile }
          : delta
      replaceDeepState(sid, resolvedDelta)
    }
    pending.clear()
    pendingSnapshotActiveFileBaselineRef.current.clear()
    snapshotThrottleRef.current = null
  }, [explorerStore, replaceDeepState])

  const handleDeepSnapshot = useCallback(
    (msg: DeepStateSnapshotMessage, sid: string, requestGenerationId: string) => {
      const { seq, todos, files: rawFiles, token_budget } = msg.data

      // Ignore out-of-order snapshots — check both the live store slot and
      // any in-flight pending delta for this same session.
      const liveDeepState = getDeepStateForSession(explorerStore.getState(), sid)
      const disposalGeneration = getChatRequestSessionDisposalGeneration(explorerStore, sid)
      const pendingEntry = pendingSnapshotRef.current.get(sid)
      const pendingForSid =
        pendingEntry?.disposalGeneration === disposalGeneration &&
        pendingEntry.requestGenerationId === requestGenerationId
          ? pendingEntry.delta
          : undefined
      const pendingSeq = pendingForSid?.snapshot_seq ?? 0
      const currentSeq = Math.max(liveDeepState.snapshot_seq, pendingSeq)
      if (seq !== undefined && seq <= currentSeq) return

      // Backend sends files as dict {path: content} — convert to DeepFile[]
      let files: DeepFile[] | undefined
      if (rawFiles != null) {
        if (Array.isArray(rawFiles)) {
          // Already array (future-proof)
          files = rawFiles as DeepFile[]
        } else if (typeof rawFiles === "object") {
          // Dict format from backend — value can be:
          //   string (plain content) OR object {content: "...", ...} (deepagents metadata)
          const now = Date.now()
          files = Object.entries(rawFiles as Record<string, unknown>).map(([path, value]) => {
            let content: string
            if (typeof value === "string") {
              content = value
            } else if (value && typeof value === "object" && "content" in value) {
              content = String((value as Record<string, unknown>).content ?? "")
            } else {
              content = JSON.stringify(value, null, 2)
            }
            return { path, content, lastModified: now }
          })
        }
      }

      // Determine if agent just wrote a file — auto-focus it unless user manually selected.
      // Precedence: /deliverable.md (newly appeared) > user's current selection > latest file.
      const previousFiles =
        (pendingForSid?.files as DeepExplorerSessionState["files"] | undefined) ??
        liveDeepState.files
      const hasActiveFileBaseline = pendingSnapshotActiveFileBaselineRef.current.has(sid)
      const activeFileBaseline = pendingSnapshotActiveFileBaselineRef.current.get(sid)
      const hasNewerLiveSelection =
        hasActiveFileBaseline && liveDeepState.activeFile !== activeFileBaseline
      let currentActiveFile = liveDeepState.activeFile
      if (!hasNewerLiveSelection && pendingForSid && "activeFile" in pendingForSid) {
        currentActiveFile = pendingForSid.activeFile ?? null
      }
      const newFiles = files ?? previousFiles
      let nextActiveFile = currentActiveFile

      const deliverableAppeared =
        !!files &&
        files.some((f) => f.path === "/deliverable.md") &&
        !previousFiles.some((f) => f.path === "/deliverable.md")

      const planAppeared =
        !!files &&
        files.some((f) => f.path === "/plan.md") &&
        !previousFiles.some((f) => f.path === "/plan.md")

      if (deliverableAppeared) {
        nextActiveFile = "/deliverable.md"
      } else if (planAppeared) {
        // Auto-focus /plan.md the moment it's first written so the user sees
        // the plan the agent is about to ask them to approve.
        nextActiveFile = "/plan.md"
      } else if (files) {
        if (files.length === 0) {
          nextActiveFile = null
        } else {
          const sorted = [...files].sort((a, b) => b.lastModified - a.lastModified)
          if (!currentActiveFile || !files.find((f) => f.path === currentActiveFile)) {
            nextActiveFile = sorted[0]?.path ?? null
          }
        }
      }

      const previousTodos =
        (pendingForSid?.todos as DeepExplorerSessionState["todos"] | undefined) ??
        liveDeepState.todos
      const nextTodos =
        todos !== undefined
          ? mergeDeepTodosForSnapshot({
              previous: previousTodos,
              incoming: todos as DeepExplorerSessionState["todos"],
            })
          : undefined

      const partial: Partial<DeepExplorerSessionState> = {
        ...(seq !== undefined && { snapshot_seq: seq }),
        ...(nextTodos !== undefined && { todos: nextTodos }),
        ...(files !== undefined && {
          files: newFiles as DeepExplorerSessionState["files"],
          activeFile: nextActiveFile,
        }),
        ...(token_budget !== undefined && { token_budget }),
      }

      if ("activeFile" in partial) {
        pendingSnapshotActiveFileBaselineRef.current.set(sid, liveDeepState.activeFile)
      }

      // Accumulate into pending bucket for this session id (later snapshot wins)
      pendingSnapshotRef.current.set(sid, {
        disposalGeneration,
        requestGenerationId,
        delta: { ...(pendingForSid ?? {}), ...partial },
      })

      if (snapshotThrottleRef.current === null) {
        snapshotThrottleRef.current = setTimeout(flushPendingSnapshot, 200)
      }
    },
    [explorerStore, flushPendingSnapshot]
  )

  // Clean up throttle timer on unmount
  useEffect(() => {
    return () => {
      if (snapshotThrottleRef.current !== null) {
        clearTimeout(snapshotThrottleRef.current)
      }
    }
  }, [])

  // ── Deep state rehydration on session open ───────────────────────────────
  // When the user opens or reloads a chat, fetch the last-persisted Deep state
  // from the LangGraph checkpointer and populate the side panel. Activity log
  // and sources aren't in the checkpoint — they repopulate on the next turn.
  const hydratedSessionRef = useRef<string | null>(null)
  useEffect(() => {
    if (!currentSessionId) return
    // Same capability guard as the message-driven rehydration effects below:
    // session ids are shared across modules, so a session first run in Deep
    // Explorer can be opened under a module whose injected store has no
    // deep-state slice — the checkpoint then comes back had_state=true and
    // the mutator call would crash. Skipping also saves the pointless
    // checkpoint fetch for those sessions.
    if (typeof replaceDeepState !== "function") return
    // Guard against double-fetches for the same session (e.g. re-renders).
    if (hydratedSessionRef.current === currentSessionId) return
    hydratedSessionRef.current = currentSessionId

    let cancelled = false
    const disposalGeneration = getChatRequestSessionDisposalGeneration(
      explorerStore,
      currentSessionId
    )
    const expectedRequestGenerationId = getChatRequestGenerationId(explorerStore, currentSessionId)
    void getDeepStateApi(currentSessionId, settingsService.token).then((res) => {
      const responseGenerationId = res.request_generation_id
      if (
        cancelled ||
        !isChatRequestSessionDisposalGenerationCurrent(
          explorerStore,
          currentSessionId,
          disposalGeneration
        ) ||
        isChatRequestSessionDisposed(explorerStore, currentSessionId) ||
        !res.had_state ||
        (responseGenerationId
          ? !adoptChatRequestGeneration(explorerStore, currentSessionId, {
              requestGenerationId: responseGenerationId,
              expectedRequestGenerationId,
            })
          : getChatRequestGenerationId(explorerStore, currentSessionId) !== undefined)
      )
        return

      const authoritativeRequestGenerationId =
        res.agent_run_request_generation_id ?? res.live_request_generation_id
      const checkpointMatchesCurrent = res.checkpoint_request_generation_id
        ? !responseGenerationId || res.checkpoint_request_generation_id === responseGenerationId
        : !authoritativeRequestGenerationId
      const liveMatchesCurrent = res.live_request_generation_id
        ? !responseGenerationId || res.live_request_generation_id === responseGenerationId
        : !res.agent_run_request_generation_id
      const now = Date.now()
      const unwrap = (value: unknown): string => {
        if (typeof value === "string") return value
        if (value && typeof value === "object" && "content" in value) {
          return String((value as Record<string, unknown>).content ?? "")
        }
        return JSON.stringify(value, null, 2)
      }
      const checkpointFiles = checkpointMatchesCurrent ? res.files : {}
      const checkpointPreviousFiles = checkpointMatchesCurrent ? res.file_prev_content : {}
      const hydratedFiles: DeepFile[] = Object.entries(checkpointFiles).map(([path, value]) => ({
        path,
        content: unwrap(value),
        previous_content:
          checkpointPreviousFiles[path] != null ? unwrap(checkpointPreviousFiles[path]) : null,
        lastModified: now,
      }))

      const activeFile = hydratedFiles.some((f) => f.path === "/plan.md")
        ? "/plan.md"
        : (hydratedFiles[0]?.path ?? null)

      // /followups.json is preserved in the LangGraph checkpoint and rides
      // along in res.files, but the WebSocket-side replay frame
      // (deep_followup_suggestions) only fires during a live run. Without
      // this re-parse, the SmartFollowupCards component sees an empty
      // followupSuggestions array on reload and renders nothing — even
      // though the data is right there in the VFS.
      //
      // Accept both `/followups.json` and `followups.json` — the backend
      // VFS has been observed to store either form (see the matching
      // `files.get("/followups.json") or files.get("followups.json")`
      // tolerance in streaming.py::_extract_followups).
      const followupsFile = hydratedFiles.find(
        (f) => f.path === "/followups.json" || f.path === "followups.json"
      )
      const followupSuggestions = parseFollowupsFromFile(followupsFile?.content)

      // Live snapshot — present when the run is in flight on another tab.
      // Hydrate the activity log + sources from it so the second tab
      // populates immediately, before the polling effect even fires.
      // Falls back to an empty list when the run has terminated and
      // `agent_run.live` was cleared.
      const liveActivityEvents = (liveMatchesCurrent
        ? (res.live_activity_events ?? [])
        : []) as unknown as DeepActivityEvent[]
      const livePatch =
        liveActivityEvents.length > 0
          ? {
              activityLog: liveActivityEvents,
              sources: deriveSourcesFromActivity(liveActivityEvents),
            }
          : {}

      if (checkpointMatchesCurrent || liveActivityEvents.length > 0) {
        replaceDeepState(currentSessionId, {
          ...(checkpointMatchesCurrent
            ? {
                todos: mergeDeepTodosForSnapshot({
                  previous: getDeepStateForSession(explorerStore.getState(), currentSessionId)
                    .todos,
                  incoming: res.todos as DeepExplorerSessionState["todos"],
                }),
                files: hydratedFiles,
                snapshot_seq: res.snapshot_seq,
                token_budget: res.token_budget,
                activeFile,
                followupSuggestions,
                // Cross-tab approval sync: if the current turn's plan has already
                // been approved on another tab, seed planApproved=true so a buffered
                // ``deep_plan_proposed`` frame replayed by the broadcaster can't
                // re-render the Approve/Reject UI here.
                ...(res.plan_approved ? { planApproved: true } : {}),
              }
            : {}),
          ...livePatch,
        })
      }
      // Only a run that is still going (live progress, or a broadcaster slot
      // parked on plan approval / ask_user) switches Deep on from here. A
      // finished run's checkpoint outlives it and the chat may have gone back
      // to plain turns since; the history restore below settles those (#3060).
      const liveArtifacts = (liveMatchesCurrent
        ? (res.live_artifacts ?? [])
        : []) as unknown as Artifact[]
      if (res.broadcaster_active || liveActivityEvents.length > 0 || liveArtifacts.length > 0) {
        liveDeepRunSessionRef.current = currentSessionId
        setEnableDeep(true)
      }

      // Live workspace artifacts — present mid-run for sessions whose
      // run started in another tab. Same hydration shape as the saved
      // message context path; the workspace store dedupes by id, so
      // it's safe to push these and let WS frames overwrite later.
      if (liveArtifacts.length > 0) {
        hydrateArtifactsFromContext(currentSessionId, liveArtifacts)
      }

      // Broadcaster slot is alive — fire a reattach WS so frames flow
      // at sub-100ms latency instead of polling cadence. This catches
      // pre-approval runs (waiting on plan / ask_user) which don't yet
      // have an agent_run row, so the page's reattach effect (gated on
      // isCurrentSessionAgentRunActive) wouldn't otherwise fire for
      // them. No-op when the slot is gone (process restart, deploy);
      // polling fills the gap.
      if (res.broadcaster_active) {
        reattachToActiveRun(currentSessionId, responseGenerationId ?? undefined)
      }

      // Phase 8.2 — backwards-compat shim. Legacy chats persist their
      // deliverable as the virtual file `/deliverable.md` instead of as a
      // typed `document` artifact in `context.artifacts`. The Workspace
      // layout reads from the artifact store, so without this synthesis
      // step those chats reload as "Workspace empty" — see
      // `.workspace-spec/WORKSPACE_CONTRACT.md` (Out of scope: Migration).
      //
      // We only synthesise when the session has no `document` artifact yet,
      // so genuine emissions always win over the synthesized fallback.
      // The id is deterministic (derived from `turn_id`) so re-running this
      // path on a re-render is idempotent.
      const deliverableFile = hydratedFiles.find(
        (f) => f.path === "/deliverable.md" && f.content.trim().length > 0
      )
      if (deliverableFile) {
        const storeState = explorerStore.getState()
        const existingArtifacts = storeState.artifactsByConversation[currentSessionId] ?? []
        const hasDocumentArtifact = existingArtifacts.some((a) => a.type === "document")
        if (!hasDocumentArtifact) {
          // Pick the most recent assistant message id as the legacy turn id —
          // it's the closest stable handle we have to the turn that produced
          // this deliverable. Fall back to the session id when no message has
          // a `messageId` (very early chats).
          const sessionMessages = storeState.sessionStates[currentSessionId]?.messages ?? []
          let legacyTurnId: string | null = null
          for (let i = sessionMessages.length - 1; i >= 0; i--) {
            const candidate = sessionMessages[i]?.[1]?.messageId
            if (typeof candidate === "string" && candidate.length > 0) {
              legacyTurnId = candidate
              break
            }
          }
          const turnId = legacyTurnId ?? `session-${currentSessionId}`
          const synthesized = synthesizeDocumentFromDeliverable({
            body: deliverableFile.content,
            conversationId: currentSessionId,
            turnId,
            createdAt: new Date().toISOString(),
          })
          appendArtifact(currentSessionId, synthesized)
          logger.info(
            `legacy /deliverable.md → synthesized document artifact id=${synthesized.id} for session=${currentSessionId}`
          )
        }
      }
    })

    return () => {
      cancelled = true
    }
  }, [
    currentSessionId,
    replaceDeepState,
    setEnableDeep,
    settingsService,
    appendArtifact,
    explorerStore,
  ])

  // ── Activity log + sources rehydration ───────────────────────────────────
  // The LangGraph checkpoint (`getDeepStateApi` above) deliberately excludes
  // the activity log and sources ribbon — those are ephemeral stream events.
  // They now live on each assistant message's `context.deep_activity_events`
  // (persisted by explorer_deep.py → save_message). On session open, walk the
  // loaded messages in chronological order, concatenate their activity
  // payloads, and derive the sources ribbon from the same events — mirroring
  // the live WebSocket path in the `deep_activity_event` handler below.
  //
  // Legacy chats (saved before the persistence change) carry only the slim
  // `activities` array; we map their tool_start/tool_end entries into
  // DeepActivityEvent skeletons so the panel is non-empty even without
  // input/result summaries.
  const sessionMessages = explorerStore((state) =>
    currentSessionId ? state.sessionStates[currentSessionId]?.messages : undefined
  )
  const hydratedActivitySessionRef = useRef<string | null>(null)
  useEffect(() => {
    if (!currentSessionId) return
    if (!sessionMessages || sessionMessages.length === 0) return
    // Modules that inject their own store via `useStore` (Dash) compose only
    // the chat + explorer-features slices — no deep-state slice. This effect
    // fires for their sessions too (the trigger is "messages just became
    // non-empty", i.e. the first completed turn), so calling the missing
    // mutator would throw and trip the route error boundary.
    if (typeof replaceDeepState !== "function") return
    if (hydratedActivitySessionRef.current === currentSessionId) return
    hydratedActivitySessionRef.current = currentSessionId

    const events: DeepActivityEvent[] = []
    for (const [, response] of sessionMessages) {
      const persisted = response?.context?.deep_activity_events
      if (persisted && persisted.length > 0) {
        for (const raw of persisted) {
          if (raw.event === "tool_start" || raw.event === "tool_end") {
            events.push({
              id: raw.id,
              event: raw.event,
              tool: raw.tool ?? "",
              source: raw.source ?? "deep_explorer",
              dispatch_id: raw.dispatch_id ?? null,
              timestamp: raw.timestamp ?? "",
              input_summary: raw.input_summary,
              result_summary: raw.result_summary,
            })
          } else if (raw.event === "truncated") {
            // Keep the truncation sentinel verbatim — ActivityLog renders it
            // as a muted "N earlier events omitted" row.
            events.push(raw as unknown as DeepActivityEvent)
          } else if (raw.event === "status") {
            // QA #C3: status events were silently dropped on session reload.
            // _make_status_event puts label/level/translation_key/artifact_id
            // inside result_summary on the wire; older rows may have them at
            // top-level. Prefer result_summary, then fall back so the chosen
            // shape always survives the round-trip.
            const rs = (raw.result_summary ?? {}) as Record<string, unknown>
            const result_summary: Record<string, unknown> = {
              label: rs.label ?? raw.label,
              level: rs.level ?? raw.level ?? "info",
              translation_key: rs.translation_key ?? raw.translation_key,
              artifact_id: rs.artifact_id ?? raw.artifact_id,
            }
            events.push({
              id: raw.id,
              event: "status",
              tool: raw.tool ?? "",
              source: raw.source ?? "deep_explorer",
              dispatch_id: raw.dispatch_id ?? null,
              timestamp: raw.timestamp ?? "",
              result_summary,
            })
          }
        }
        continue
      }
      // Legacy fallback: slim activities → skeletal DeepActivityEvents.
      const legacy = response?.activities
      if (legacy && legacy.length > 0) {
        for (const act of legacy) {
          // `act` is loosely typed in the response; widen to unknown-bag reads
          // so we can tolerate both current and older shapes.
          const bag = act as unknown as Record<string, unknown>
          const activityType = bag.activity_type as string | undefined
          if (activityType !== "tool_start" && activityType !== "tool_end") continue
          const details = (bag.details as Record<string, unknown> | undefined) ?? {}
          // `ActivityMessage.timestamp` is a number in the current schema,
          // but pre-schema messages may have stored it as a string. Coerce
          // both shapes to the ISO-string timestamp that `DeepActivityEvent`
          // expects downstream.
          const timestampStr = legacyTimestampString(bag.timestamp)
          const idSeed = (bag.id as string | undefined) ?? `legacy-${events.length}-${timestampStr}`
          events.push({
            id: idSeed,
            event: activityType,
            tool: String(details.tool ?? bag.message ?? "tool"),
            source: String(details.source ?? "deep_explorer"),
            dispatch_id: (details.dispatch_id as string | null | undefined) ?? null,
            timestamp: timestampStr,
          })
        }
      }
    }

    if (events.length === 0) {
      replaceDeepState(currentSessionId, { activityLog: [], sources: [] })
      return
    }

    // Settle any orphan tool_start events. The persisted list may drop a
    // matching tool_end when the turn hit the 2 MB Cosmos ceiling or was
    // cut short. A lone tool_start would otherwise render as a perpetual
    // spinner on reopen even though the turn finished long ago.
    const starts = new Set<string>()
    const ends = new Set<string>()
    for (const e of events) {
      if (e.event === "tool_start") starts.add(e.id)
      else if (e.event === "tool_end") ends.add(e.id)
    }
    for (const id of starts) {
      if (!ends.has(id)) {
        const start = events.find((e) => e.id === id && e.event === "tool_start")
        if (!start) continue
        events.push({
          id,
          event: "tool_end",
          tool: start.tool,
          source: start.source,
          dispatch_id: start.dispatch_id ?? null,
          timestamp: start.timestamp,
        })
      }
    }

    replaceDeepState(currentSessionId, {
      activityLog: events,
      sources: deriveSourcesFromActivity(events),
    })
  }, [currentSessionId, sessionMessages, replaceDeepState, explorerStore])

  // ── Workspace artifacts rehydration ──────────────────────────────────────
  // Mirror of the activity-events hydration above, but for the typed artifact
  // contract (Phase 2.1). Each persisted assistant message may carry
  // `context.artifacts` — concatenate them in chronological order and replace
  // the session's slot in the artifacts slice. See
  // `.workspace-spec/WORKSPACE_CONTRACT.md` (Persistence schema).
  const hydratedArtifactsSessionRef = useRef<string | null>(null)
  useEffect(() => {
    if (!currentSessionId) return
    if (!sessionMessages || sessionMessages.length === 0) return
    // Same capability guard as the activity hydration above: stores injected
    // by non-Explorer modules (Dash) lack the artifacts slice.
    if (typeof hydrateArtifactsFromContext !== "function") return
    if (hydratedArtifactsSessionRef.current === currentSessionId) return
    hydratedArtifactsSessionRef.current = currentSessionId

    const artifacts: Artifact[] = []
    for (const [, response] of sessionMessages) {
      const persisted = response?.context?.artifacts
      if (persisted && persisted.length > 0) {
        artifacts.push(...persisted)
      }
    }
    hydrateArtifactsFromContext(currentSessionId, artifacts)
  }, [currentSessionId, sessionMessages, hydrateArtifactsFromContext])

  // Reset the artifact hydrate gate on session change.
  useEffect(() => {
    if (!currentSessionId) {
      hydratedArtifactsSessionRef.current = null
    }
  }, [currentSessionId])

  // Reset the per-session activity hydrate gate when the session id changes
  // so switching chats triggers a fresh replay instead of re-using stale state.
  useEffect(() => {
    if (!currentSessionId) {
      hydratedActivitySessionRef.current = null
    }
  }, [currentSessionId])

  // Same for the Deep state fetch: the live-run marker is dropped when leaving a
  // chat, so reopening it must re-check for a run still waiting on plan approval
  // or ask_user (no agent_run row yet) instead of trusting saved answers alone.
  useEffect(() => {
    if (!currentSessionId) {
      hydratedSessionRef.current = null
    }
  }, [currentSessionId])

  // ── Deep mode follows the chat (#3060) ──────────────────────────────────
  // Opening a chat from history (sidebar, link, reload, restored tab) gives it
  // the mode of its latest saved answer: Deep if that answer came from Deep
  // Explorer (or its run is still going), plain otherwise. Keying on any
  // earlier Deep turn reopened chats in Deep after the user had switched back
  // to plain. Only a history load (`isHydrating`) arms this, so a chat created
  // by sending keeps the mode it was sent with, and it settles once per open,
  // so the kill-switch or a hand toggle afterwards can't ping-pong with it.
  const isCurrentSessionHydrating = explorerStore((state) =>
    currentSessionId ? (state.sessionStates[currentSessionId]?.isHydrating ?? false) : false
  )
  // Arm only on the transition into a history load: re-arming on every render
  // while it loads would overrule a Deep choice the user made in the meantime.
  useEffect(() => {
    if (currentSessionId && isCurrentSessionHydrating) {
      deepModeRestorePendingSessionRef.current = currentSessionId
    }
  }, [currentSessionId, isCurrentSessionHydrating])
  useEffect(() => {
    if (!currentSessionId || isCurrentSessionHydrating) return
    if (deepModeRestorePendingSessionRef.current !== currentSessionId) return
    const sentTurn = sentTurnModeRef.current.get(currentSessionId)
    const liveSentTurnDeep =
      sentTurn &&
      getChatRequestControl(explorerStore, currentSessionId)?.requestGenerationId ===
        sentTurn.requestGenerationId
        ? sentTurn.deep
        : undefined
    const openInDeep =
      forcedDeepSessionRef.current === currentSessionId ||
      (liveSentTurnDeep ??
        (shouldOpenSessionInDeepMode(sessionMessages) ||
          liveDeepRunSessionRef.current === currentSessionId))
    // `isDeepExplorerEnabled` reads false until /api/config resolves; wait for
    // it instead of settling a Deep chat into plain mode.
    if (openInDeep && !config) return
    deepModeRestorePendingSessionRef.current = null
    if (forcedDeepSessionRef.current === currentSessionId) {
      forcedDeepSessionRef.current = null
    }
    const nextEnableDeep = openInDeep && isDeepExplorerEnabled && !isDeepRouteLocked
    if (explorerStore.getState().enableDeep !== nextEnableDeep) {
      setEnableDeep(nextEnableDeep)
    }
  }, [
    currentSessionId,
    isCurrentSessionHydrating,
    sessionMessages,
    config,
    isDeepExplorerEnabled,
    isDeepRouteLocked,
    explorerStore,
    setEnableDeep,
  ])

  // Leaving a chat for a blank one (New chat, the Explorer start page, a
  // deleted chat) starts plain; a live-run marker never outlives its chat.
  const previousDeepModeSessionIdRef = useRef(currentSessionId)
  useEffect(() => {
    const previousSessionId = previousDeepModeSessionIdRef.current
    if (previousSessionId === currentSessionId) return
    previousDeepModeSessionIdRef.current = currentSessionId
    liveDeepRunSessionRef.current = null
    // An override naming the chat being opened is why we are here; only a stale
    // one, left over for some other chat, gets dropped.
    if (forcedDeepSessionRef.current !== currentSessionId) {
      forcedDeepSessionRef.current = null
    }
    if (previousSessionId && !currentSessionId) {
      deepModeRestorePendingSessionRef.current = null
      setEnableDeep(false)
    }
  }, [currentSessionId, setEnableDeep])

  // ── Plan decision sender ──────────────────────────────────────────────────
  // Sends a deep_plan_decision frame to the backend over the active deep WS.
  const sendPlanDecision = useCallback(
    (
      decision: "approve" | "cancel" | "revise",
      extra?: {
        feedback?: string
        editedContent?: string
        targetSessionId?: string | null
      }
    ): boolean => {
      const targetSid = extra?.targetSessionId ?? explorerStore.getState().currentSessionId
      const send = getDeepWsSend(targetSid)
      // Surface send failures to the user so an unresponsive Approve/Reject/Revise
      // button isn't silently swallowed. Keep `planProposed` truthy on failure so
      // the user can retry from the same UI state after reconnecting.
      const showSendError = () => {
        toast.error(
          i18n.t("deepExplorer.plan_decision_send_failed", {
            defaultValue:
              "Couldn't reach the assistant. Check your connection and try the plan button again.",
          })
        )
      }
      if (!send) {
        logger.warn(
          `sendPlanDecision: no active deep WebSocket connection (decision=${decision}, session=${targetSid ?? "none"})`
        )
        showSendError()
        return false
      }
      const payload: Record<string, unknown> = { type: "deep_plan_decision", decision }
      if (decision === "revise" && extra?.feedback) {
        payload.feedback = extra.feedback
      }
      // Forward edited /plan.md content on approve so the executor runs the
      // user's edits, not the planner's original draft. The backend writes
      // this into the graph VFS before resuming with approve.
      if (decision === "approve" && typeof extra?.editedContent === "string") {
        payload.edited_plan = extra.editedContent
      }
      try {
        send(payload)
        logger.info(`sendPlanDecision: dispatched decision=${decision} for session=${targetSid}`)
      } catch (err) {
        logger.warn("sendPlanDecision: failed to send", err as Error)
        showSendError()
        return false
      }

      // Approve dismisses the gate here, after the frame is accepted. Cancel
      // does not: the caller clears only when this returns true, and a failure
      // return must leave planProposed and proposedTodos untouched.
      if (decision === "approve") {
        // UI-5: capture a snapshot of what the user just approved so the
        // rewind affordance has something to restore to. We grab the plan
        // markdown (so a re-edit starts from the exact text the user saw)
        // plus the proposed todos. Also store the edited content if the
        // user passed it, since that's the actual plan being executed.
        const snapState = explorerStore.getState()
        const sid = targetSid
        if (!sid) return true
        setDeepPlanApproved(sid, true)
        // Clear the proposed-plan banner. The side-panel wrapper
        // (handlePlanDecision) already does this on the foreground approve
        // path, but the **Run in background** flow calls us directly and
        // would otherwise leave `planProposed` stuck at true — so the
        // approval UI never goes away and TodoList keeps rendering the
        // mapped-to-pending proposedTodos instead of the live snapshot
        // todos (Bundle C follow-up: Erik 2026-05-04 retest).
        setDeepPlanProposed(sid, false)
        const sessionDeep = getDeepStateForSession(snapState, sid)
        const planFile = sessionDeep.files.find(
          (f) => f.path === "/plan.md" || f.path === "plan.md"
        )
        const snapshotContent =
          typeof extra?.editedContent === "string" ? extra.editedContent : (planFile?.content ?? "")
        snapState.replaceDeepState(sid, {
          planSnapshot: {
            todos: sessionDeep.proposedTodos.map((todo) => ({ ...todo })),
            planContent: snapshotContent,
            takenAt: Date.now(),
          },
        })
        // Defensive backstop for legacy/older backends that may still emit
        // pre-approval prose on the regular `chunk` channel: if any content
        // accumulated before approval, fold it into `thinking` so the answer
        // bubble starts fresh. New backend versions route pre-approval text
        // via `deep_planning_chunk` directly into `thinking`, in which case
        // `current[1].content` will already be empty here and this is a no-op.
        const state = explorerStore.getState()
        const session = state.sessionStates[sid]
        const current = session?.streamingAnswer
        if (current && current[1].content) {
          const [q, answer] = current
          const priorThinking = answer.thinking ?? ""
          const merged = priorThinking ? `${priorThinking}\n\n${answer.content}` : answer.content
          state.updateSession(sid, {
            streamingAnswer: [q, { ...answer, thinking: merged, content: "" }],
          })
        }
      }
      return true
    },
    [explorerStore, getDeepWsSend, setDeepPlanApproved, setDeepPlanProposed]
  )

  // ── Artifact (HITL) decision sender — Phase 9.2 ───────────────────────────
  // Mirrors sendPlanDecision but for the per-tool-action approval gate
  // emitted as a `pending_action` artifact. The backend's
  // `request_action_approval` helper waits for a `deep_artifact_decision`
  // frame matching the artifact id before proceeding (or rolling back).
  const sendArtifactDecision = useCallback(
    (decision: "approve" | "reject", artifactId: string, targetSessionId?: string | null) => {
      const targetSid = targetSessionId ?? explorerStore.getState().currentSessionId
      const send = getDeepWsSend(targetSid)
      const showSendError = () => {
        toast.error(
          i18n.t("deepExplorer.artifact_decision_send_failed", {
            defaultValue:
              "Couldn't reach the assistant. Check your connection and try the action button again.",
          })
        )
      }
      if (!send) {
        logger.warn(
          `sendArtifactDecision: no active deep WebSocket connection (decision=${decision}, artifact=${artifactId}, session=${targetSid ?? "none"})`
        )
        showSendError()
        return
      }
      const payload: Record<string, unknown> = {
        type: "deep_artifact_decision",
        artifact_id: artifactId,
        decision,
      }
      try {
        send(payload)
        logger.info(
          `sendArtifactDecision: dispatched decision=${decision} for artifact=${artifactId} session=${targetSid}`
        )
      } catch (err) {
        logger.warn("sendArtifactDecision: failed to send", err as Error)
        showSendError()
      }
    },
    [explorerStore, getDeepWsSend]
  )

  // ── Push-side note / btw interjection (UI-6) ──────────────────────────────
  // Lets the user push a side note into a running agent. NOTE: the backend
  // has no consumer for the `deep_user_note` control frame yet — the deep WS
  // client-message loop whitelists only plan / ask_user / tool / artifact
  // decisions and drops everything else (see websocket_decorators.py). Until
  // the mid-run HumanMessage injection consumer ships, emitting the frame is a
  // no-op that never reaches the agent, so we must NOT report success (doing so
  // flashed a phantom "sent" state and silently lost the user's instruction).
  // Return false and surface an honest message instead. Signature stays
  // `(note: string) => boolean` so re-enabling later is a one-line change.
  const sendUserNote = useCallback((note: string): boolean => {
    const trimmed = note.trim()
    if (trimmed.length === 0) return false
    logger.warn(
      `sendUserNote: 'deep_user_note' has no backend consumer — dropping note (${trimmed.length} chars) instead of reporting a false success`
    )
    toast.error(
      i18n.t("deepExplorer.btw_unavailable", {
        defaultValue: "Notes to a running agent aren't available yet.",
      })
    )
    return false
  }, [])

  // ── Rewind to plan (UI-5) ─────────────────────────────────────────────────
  // DISABLED — the client cannot halt an executing deep run. The rewind button
  // only shows after the plan is approved and the run has begun; at that point
  // the graph is detached from the WS via dispatch_via_broadcaster
  // (explorer_ws.py), and the backend has NO ``deep_plan_rewind`` handler
  // (client_message_loop drops it — websocket_decorators.py:308-309, mirroring
  // the inert ``deep_user_note`` documented there).
  //
  // The previous implementation sent that inert frame and then optimistically
  // re-opened the plan editor locally (planProposed=true / planApproved=false).
  // That was a lie: the server-side run kept executing, deep_state_snapshot /
  // artifact frames + the 2.5s live poll kept mutating the workspace under the
  // user, the deliverable was still written, and agent_run still flipped to
  // done — directly contradicting the "rewound" UI (and a subsequent re-approve
  // had no waiter to reach, so it was inert too).
  //
  // Until the backend can genuinely interrupt a running graph, surface that
  // rewind isn't available mid-run instead of faking it. Re-enable the local
  // restore + frame dispatch once a server-side halt path exists.
  const rewindToPlan = useCallback(() => {
    logger.warn("rewindToPlan: disabled — backend cannot halt an executing deep run")
    toast.info(
      i18n.t("deepExplorer.rewind_unavailable", {
        defaultValue:
          "Rewinding to the plan isn't available once the run has started. Send a new message to change the plan.",
      })
    )
  }, [])

  // ── Ask-user response sender ──────────────────────────────────────────────
  // Sends a deep_ask_user_response frame to the backend over the active deep WS.
  const sendAskUserResponse = useCallback(
    (
      answers: Record<string, string | string[]>,
      targetSessionId?: string | null,
      askId?: string | null
    ) => {
      const targetSid = targetSessionId ?? explorerStore.getState().currentSessionId
      const pendingBeforeSend = getDeepStateForSession(
        explorerStore.getState(),
        targetSid
      ).askUserPayload
      const responseAskId = askId ?? pendingBeforeSend?.ask_id
      const send = getDeepWsSend(targetSid)
      // Surface send failures to the user so a dropped WS doesn't silently
      // discard their answers. Keep `askUserProposed` truthy on failure so the
      // form stays open and the user can retry after reconnecting — otherwise
      // the backend ask_user waiter times out and continues without answers.
      const showSendError = () => {
        toast.error(
          i18n.t("deepExplorer.ask_user_send_failed", {
            defaultValue:
              "Couldn't reach the assistant. Check your connection and submit your answers again.",
          })
        )
      }
      if (!send) {
        logger.warn(
          `sendAskUserResponse: no active deep WebSocket connection for session=${targetSid ?? "none"}`
        )
        showSendError()
        return
      }
      try {
        send({
          type: "deep_ask_user_response",
          data: { ask_id: responseAskId, answers },
        })
      } catch (err) {
        logger.warn("sendAskUserResponse: failed to send", err as Error)
        showSendError()
        return
      }
      if (targetSid) {
        const stillPending = getDeepStateForSession(
          explorerStore.getState(),
          targetSid
        ).askUserPayload
        if (stillPending?.ask_id === responseAskId) {
          setAskUserProposed(targetSid, false)
        }
      }
    },
    [explorerStore, getDeepWsSend, setAskUserProposed]
  )

  const skipAskUser = useCallback(() => {
    sendAskUserResponse({})
  }, [sendAskUserResponse])

  // Per-session monotonic seq tracking for the run broadcaster's reconnect
  // path. Every chunk leaving the backend's dispatch_via_broadcaster
  // carries a ``_seq`` (run_broadcaster.py:_attach_seq); we track the max
  // we've seen here so a reattach WS can pass it back as last_seen_seq
  // and skip already-shown frames. Plain refs (not store state) — these
  // are pure transport metadata, not something rendering depends on.
  const lastSeenSeqRef = useRef<Record<string, { requestGenerationId: string; seq: number }>>({})

  // ── Composite message handler ─────────────────────────────────────────────
  const onMessage = useCallback(
    (
      message: ServerMessage,
      context?: {
        sessionId?: string | null
        isActive?: boolean
        requestToken?: symbol
        requestGenerationId?: string
        disposalGeneration?: number
      }
    ) => {
      // Each WS captures its session id in context. Save/title frames also carry
      // an authoritative id, which wins when a shared connection context differs.
      const contextSid = context?.sessionId ?? explorerStore.getState().currentSessionId
      const targetSid =
        message.type === "message_saved" || message.type === "title_state_changed"
          ? (message.data.session_id ?? contextSid)
          : contextSid
      const frameGenerationId = (message as unknown as { _request_generation_id?: string })
        ._request_generation_id
      // Resolve and guard before activity, seq, cache, toast, or store effects.
      if (
        targetSid &&
        (isChatRequestSessionDisposed(explorerStore, targetSid) ||
          (context?.requestToken &&
            !chatRequestOwnsSession(explorerStore, targetSid, context.requestToken)) ||
          (context?.disposalGeneration !== undefined &&
            !isChatRequestSessionDisposalGenerationCurrent(
              explorerStore,
              targetSid,
              context.disposalGeneration
            )) ||
          (context?.requestGenerationId !== undefined &&
            (frameGenerationId !== context.requestGenerationId ||
              !isChatRequestGenerationCurrent(
                explorerStore,
                targetSid,
                context.requestGenerationId
              ))))
      )
        return

      // ResponseTiming is namespace-level UI telemetry for the foreground
      // turn, unlike the per-session message/deep-state slots below. A frame
      // from a backgrounded socket must still route to targetSid, but it must
      // not claim the active turn's first-activity milestone.
      const isActiveForTiming =
        context?.isActive ?? targetSid === explorerStore.getState().currentSessionId
      if (isActiveForTiming) {
        if (message.type === "activity" && !isSystemNoise(message as ActivityMessage)) {
          explorerStore.getState().markFirstActivity()
        } else if (
          message.type === "deep_activity_event" ||
          message.type === "processing_started"
        ) {
          explorerStore.getState().markFirstActivity()
        }
      }

      // Track max(_seq) per session for the broadcaster reconnect path.
      // Update before any early-return below so dropped frame types still
      // contribute to the seq count (the backend stamped them all).
      const seq = (message as unknown as { _seq?: number })._seq
      if (typeof seq === "number" && targetSid && frameGenerationId) {
        const prior = lastSeenSeqRef.current[targetSid]
        if (prior?.requestGenerationId !== frameGenerationId || seq > prior.seq) {
          lastSeenSeqRef.current[targetSid] = {
            requestGenerationId: frameGenerationId,
            seq,
          }
        }
      }

      // Modules that inject their own store via `useStore` (e.g. Dash) compose
      // only the chat + explorer-features slices — no deep-state or artifacts
      // slice — so every deep_*/artifact_* branch below would call an undefined
      // mutator (replaceDeepState, appendArtifact, applyArtifactPatch,
      // setDeepPlanProposed, …). The backend doesn't emit these frames for such
      // sessions, but a stray one would invoke undefined(...) *outside* React
      // render and white-screen the whole route via the error boundary — the
      // same failure class PR #2132 guarded for the rehydration effects. Probe
      // deep capability once and no-op deep/artifact frames when it's absent.
      const messageType = message.type as string
      if (
        typeof replaceDeepState !== "function" &&
        (messageType.startsWith("deep_") || messageType.startsWith("artifact_"))
      ) {
        return
      }

      if (message.type === "deep_state_snapshot") {
        if (!targetSid || !frameGenerationId) return
        handleDeepSnapshot(message as DeepStateSnapshotMessage, targetSid, frameGenerationId)
        return
      }
      if (message.type === "deep_citation_verification") {
        if (!targetSid) return
        const payload = (message as DeepCitationVerificationMessage).data
        const map: Record<
          string,
          { verified: boolean | null; issue?: string; actualSnippet?: string }
        > = {}
        for (const v of payload.verifications ?? []) {
          if (!v?.citation) continue
          map[v.citation] = {
            verified: v.verified ?? null,
            issue: v.issue,
            actualSnippet: v.actual_snippet,
          }
        }
        replaceDeepState(targetSid, {
          citationVerifications: map,
          verificationSummary: {
            total: payload.total ?? 0,
            verified: payload.verified ?? 0,
            unverified: payload.unverified ?? 0,
          },
        })
        return
      }
      if (message.type === "deep_followup_suggestions") {
        if (!targetSid) return
        const rawSuggestions = (message as DeepFollowupSuggestionsMessage).data.suggestions ?? []
        const suggestions = rawSuggestions
          .map((s) => ({
            question: String(s.question ?? "").trim(),
            miniPlan: Array.isArray(s.mini_plan)
              ? s.mini_plan.map((t) => String(t).trim()).filter(Boolean)
              : [],
          }))
          .filter((s) => s.question.length > 0 && s.miniPlan.length > 0)
        // FOLLOWUP-DIAG: shape inspection so we can tell the difference
        // between "frame never arrived" and "frame arrived but filtered
        // out because mini_plan was empty". Remove once the broadcaster
        // race is closed.
        logger.info(
          `FOLLOWUP-DIAG received: raw=${rawSuggestions.length} kept=${suggestions.length} sid=${targetSid}`
        )
        replaceDeepState(targetSid, { followupSuggestions: suggestions })
        return
      }
      if (message.type === "deep_deliverable_type") {
        if (!targetSid) return
        const typeMsg = message as DeepDeliverableTypeMessage
        replaceDeepState(targetSid, {
          deliverableType: typeMsg.data.deliverable_type,
          // ``execution_mode`` is optional on the wire — older backends may
          // omit it. Forward whatever arrives so the UI can surface it.
          executionMode: typeMsg.data.execution_mode ?? null,
        })
        // Auto-focus /deliverable.md so the user sees their artifact fill in.
        const sessionDeep = getDeepStateForSession(explorerStore.getState(), targetSid)
        const pendingForSid = pendingSnapshotRef.current.get(targetSid)
        const effectiveFiles =
          (pendingForSid?.delta.files as DeepExplorerSessionState["files"] | undefined) ??
          sessionDeep.files
        const hasDeliverable = effectiveFiles.some((f) => f.path === "/deliverable.md")
        if (hasDeliverable) {
          if (pendingForSid) {
            pendingSnapshotRef.current.set(targetSid, {
              ...pendingForSid,
              delta: { ...pendingForSid.delta, activeFile: "/deliverable.md" },
            })
            pendingSnapshotActiveFileBaselineRef.current.set(targetSid, "/deliverable.md")
          }
          replaceDeepState(targetSid, { activeFile: "/deliverable.md" })
        }
        return
      }
      if (message.type === "deep_subagent_chunk") {
        // B7: live tokens from a dispatched subagent. Accumulate per
        // `dispatch_id` into the running session's slot so WorkerLanes can
        // surface the streaming text under the matching `task` tool row.
        if (!targetSid) return
        const chunk = (message as DeepSubagentChunkMessage).data
        if (!chunk?.dispatch_id || !chunk.delta) return
        const live = getDeepStateForSession(explorerStore.getState(), targetSid).liveSubagentReports
        const prev = live[chunk.dispatch_id] ?? ""
        replaceDeepState(targetSid, {
          liveSubagentReports: { ...live, [chunk.dispatch_id]: prev + chunk.delta },
        })
        return
      }
      if (message.type === "deep_orchestrator_reasoning_chunk") {
        // B4: live tokens from the orchestrator's chat-model between tool
        // calls. Accumulate per run_id; first chunk for a fresh id creates
        // a new live block, subsequent chunks append.
        if (!targetSid) return
        const chunk = (message as DeepOrchestratorReasoningChunkMessage).data
        if (!chunk?.run_id || !chunk.delta) return
        // Round-2 trace: confirm chunks reach the handler when the user
        // reports no thought bubbles. console.debug gets filtered by
        // default — use info so it shows up without enabling Verbose.
        // Remove after diagnosis.
        logger.info(
          `B4 reasoning_chunk run=${chunk.run_id.slice(0, 8)} delta_len=${chunk.delta.length}`
        )
        const blocks = getDeepStateForSession(
          explorerStore.getState(),
          targetSid
        ).orchestratorReasoning
        const idx = blocks.findIndex((b) => b.id === chunk.run_id)
        if (idx === -1) {
          replaceDeepState(targetSid, {
            orchestratorReasoning: [
              ...blocks,
              {
                id: chunk.run_id,
                text: chunk.delta,
                live: true,
                followingToolId: null,
                startedAt: Date.now(),
              },
            ],
          })
        } else {
          const next = blocks.slice()
          next[idx] = { ...next[idx], text: next[idx].text + chunk.delta }
          replaceDeepState(targetSid, { orchestratorReasoning: next })
        }
        return
      }
      if (message.type === "deep_orchestrator_reasoning_complete") {
        // B4: AIMessage had tool_calls — chunks were genuine inter-tool
        // reasoning. Flip the block's live flag off; the rendered text
        // stays in place as a permanent thought bubble in the lane.
        if (!targetSid) return
        const data = (message as DeepOrchestratorReasoningCompleteMessage).data
        if (!data?.run_id) return
        const blocks = getDeepStateForSession(
          explorerStore.getState(),
          targetSid
        ).orchestratorReasoning
        const idx = blocks.findIndex((b) => b.id === data.run_id)
        if (idx === -1) return
        const next = blocks.slice()
        next[idx] = {
          ...next[idx],
          live: false,
          followingToolId: data.following_tool_id ?? null,
        }
        replaceDeepState(targetSid, { orchestratorReasoning: next })
        return
      }
      if (message.type === "deep_subagent_reasoning_chunk") {
        // Round-4: live tokens from a subagent's chat-model between its
        // own tool calls. Accumulate per (dispatch_id, run_id) so each
        // subagent lane can interleave reasoning with its tool events.
        if (!targetSid) return
        const chunk = (message as DeepSubagentReasoningChunkMessage).data
        if (!chunk?.dispatch_id || !chunk.run_id || !chunk.delta) return
        const all = getDeepStateForSession(explorerStore.getState(), targetSid).subagentReasoning
        const blocks = all[chunk.dispatch_id] ?? []
        const idx = blocks.findIndex((b) => b.id === chunk.run_id)
        const nextBlocks =
          idx === -1
            ? [
                ...blocks,
                {
                  id: chunk.run_id,
                  text: chunk.delta,
                  live: true,
                  followingToolId: null,
                  startedAt: Date.now(),
                },
              ]
            : blocks.map((b, i) => (i === idx ? { ...b, text: b.text + chunk.delta } : b))
        replaceDeepState(targetSid, {
          subagentReasoning: { ...all, [chunk.dispatch_id]: nextBlocks },
        })
        return
      }
      if (message.type === "deep_subagent_reasoning_complete") {
        if (!targetSid) return
        const data = (message as DeepSubagentReasoningCompleteMessage).data
        if (!data?.dispatch_id || !data?.run_id) return
        const all = getDeepStateForSession(explorerStore.getState(), targetSid).subagentReasoning
        const blocks = all[data.dispatch_id]
        if (!blocks) return
        const idx = blocks.findIndex((b) => b.id === data.run_id)
        if (idx === -1) return
        const nextBlocks = blocks.map((b, i) => (i === idx ? { ...b, live: false } : b))
        replaceDeepState(targetSid, {
          subagentReasoning: { ...all, [data.dispatch_id]: nextBlocks },
        })
        return
      }
      if (message.type === "deep_subagent_reasoning_discard") {
        if (!targetSid) return
        const data = (message as DeepSubagentReasoningDiscardMessage).data
        if (!data?.dispatch_id || !data?.run_id) return
        const all = getDeepStateForSession(explorerStore.getState(), targetSid).subagentReasoning
        const blocks = all[data.dispatch_id]
        if (!blocks) return
        const filtered = blocks.filter((b) => b.id !== data.run_id)
        replaceDeepState(targetSid, {
          subagentReasoning: { ...all, [data.dispatch_id]: filtered },
        })
        return
      }
      if (message.type === "deep_orchestrator_reasoning_discard") {
        // B4: AIMessage had no tool_calls — what we optimistically streamed
        // as reasoning is actually the final answer prefix and is being
        // routed to the answer bubble via the existing `chunk` channel.
        // Drop the optimistic block so it doesn't double up in the lane.
        if (!targetSid) return
        const data = (message as DeepOrchestratorReasoningDiscardMessage).data
        if (!data?.run_id) return
        const blocks = getDeepStateForSession(
          explorerStore.getState(),
          targetSid
        ).orchestratorReasoning
        if (!blocks.some((b) => b.id === data.run_id)) return
        replaceDeepState(targetSid, {
          orchestratorReasoning: blocks.filter((b) => b.id !== data.run_id),
        })
        return
      }
      if (message.type === "deep_planning_chunk") {
        // Pre-approval reasoning from the Deep Explorer planner. Append to
        // `thinking` on the current streamingAnswer so ThinkingPanel renders
        // it live — do NOT add to `content`, which would make the answer
        // bubble show partial prose before the plan is approved.
        const chunkMsg = message as DeepPlanningChunkMessage
        const delta = chunkMsg.data.content
        if (!delta) return
        const sessionId = context?.sessionId ?? explorerStore.getState().currentSessionId
        if (!sessionId) return
        const state = explorerStore.getState()
        const session = state.sessionStates[sessionId]
        const current = session?.streamingAnswer
        if (!current) return
        const [q, answer] = current
        const priorThinking = answer.thinking ?? ""
        state.updateSession(sessionId, {
          streamingAnswer: [q, { ...answer, thinking: priorThinking + delta }],
        })
        return
      }
      if (message.type === "deep_plan_proposed") {
        if (!targetSid) return
        const planMsg = message as DeepPlanProposedMessage
        const autoApprove = getDeepStateForSession(explorerStore.getState(), targetSid).autoApprove
        if (autoApprove) {
          // Auto-approve: send the decision to the backend and clear local state.
          setDeepPlanProposed(
            targetSid,
            false,
            planMsg.data.todos as DeepExplorerSessionState["todos"]
          )
          sendPlanDecision("approve", { targetSessionId: targetSid })
        } else {
          setDeepPlanProposed(
            targetSid,
            true,
            planMsg.data.todos as DeepExplorerSessionState["todos"]
          )
          // A new plan proposal supersedes any pending clarification: the
          // agent has moved past ask_user, so clear `askUserProposed` so
          // the side-panel ask_user modal and any "ask_user takes focus"
          // UI logic stops fighting the plan render.
          setAskUserProposed(targetSid, false, null)
          // Force the workspace center to focus the latest plan artifact.
          // Without this, the plan card flickered into view and was then
          // displaced by the ask_user lineage on the next render (Daniel
          // feedback 2026-05-12 — "plan rendered for a second then back to
          // the Quick Question"). This is a no-op when the plan artifact
          // hasn't arrived yet (small race window — the deep_plan_proposed
          // frame and artifact_emit can race); the auto-advance in
          // `appendArtifact` covers that case symmetrically.
          const latestPlan = getLatestPlanArtifact(explorerStore.getState(), targetSid)
          if (latestPlan) {
            focusArtifactAction(targetSid, latestPlan.id)
          }
          // Auto-focus /plan.md so the user sees the plan document in VirtualFileViewer.
          // Also patch any pending throttled snapshot for this session so it
          // can't overwrite us when it flushes ~200ms later (race between
          // snapshot + proposal frame).
          const pendingForSid = pendingSnapshotRef.current.get(targetSid)
          if (pendingForSid) {
            pendingSnapshotRef.current.set(targetSid, {
              ...pendingForSid,
              delta: { ...pendingForSid.delta, activeFile: "/plan.md" },
            })
            pendingSnapshotActiveFileBaselineRef.current.set(targetSid, "/plan.md")
          }
          replaceDeepState(targetSid, { activeFile: "/plan.md" })
        }
        return
      }
      if (message.type === "deep_tool_approval_required") {
        if (!targetSid) return
        const approvalMsg = message as DeepToolApprovalRequiredMessage
        if (!getDeepWsSend(targetSid)) {
          // Still registered: a reattach may connect before the user decides.
          logger.warn("deep_tool_approval_required received without a live Deep socket")
        }
        // Rendered by the ordinary ToolApprovalOverlay; the reply goes back
        // over the Deep socket via the registry (#2962). The sender is looked
        // up when the user decides so a reattached socket carries the answer.
        const approval = registerDeepToolApproval({
          sessionId: targetSid,
          request: approvalMsg.data,
          frameSeq: typeof approvalMsg._seq === "number" ? approvalMsg._seq : null,
          generationId: requestGenerationOf(message),
          onDecided: (requestId, status) => {
            const state = explorerStore.getState()
            const current = state.sessionStates[targetSid]?.toolApprovals ?? []
            state.updateSession(targetSid, {
              toolApprovals: current.map((item) =>
                item.request_id === requestId
                  ? { ...item, status, updated_at: new Date().toISOString() }
                  : item
              ),
            })
          },
        })
        if (!approval) return // same frame delivered twice
        const state = explorerStore.getState()
        const current = state.sessionStates[targetSid]?.toolApprovals ?? []
        state.updateSession(targetSid, {
          toolApprovals: [...current.filter((item) => item.status === "pending"), approval],
          toolApprovalSessionId: targetSid,
        })
        return
      }

      if (message.type === "deep_ask_user") {
        if (!targetSid) return
        const askMsg = message as DeepAskUserMessage
        // `proposed_todos` is an opaque list[dict] on the BE (the ask_user
        // tool emits whatever shape it has on hand), but the consumer
        // treats entries as PlanTodo-ish — id/content/text/status. Now
        // that DeepTodoStatus matches the wire format ("completed"), this
        // is a structural assertion rather than an enum coercion.
        setAskUserProposed(targetSid, true, {
          ask_id: askMsg.data.ask_id,
          context: askMsg.data.context,
          questions: askMsg.data.questions,
          proposed_todos: askMsg.data.proposed_todos as
            | DeepExplorerSessionState["todos"]
            | undefined,
        })
        return
      }
      if ((message.type as string) === "deep_plan_cancelled") {
        if (targetSid) cancelDeepToolApprovalsForSession(targetSid, requestGenerationOf(message))
        // Backend abandoned the pending plan decision (most commonly a
        // server-side wait timeout). Clear the Approve/Reject buttons from
        // the UI and notify the user so it doesn't feel like the agent
        // froze silently.
        const cancelMsg = message as unknown as {
          type: string
          data: { reason: string; message?: string }
        }
        logger.warn(`deep_plan_cancelled received (reason=${cancelMsg.data.reason})`)
        // Invalidate agent runs so the panel reflects the cancelled state
        // immediately — the backend has already written the transition.
        void queryClient.invalidateQueries({ queryKey: AGENT_RUNS_QUERY_KEY })
        if (targetSid) setDeepPlanProposed(targetSid, false)
        const fallbackText =
          cancelMsg.data.message ??
          i18n.t("deepExplorer.plan_cancelled_timeout", {
            defaultValue:
              "The assistant stopped waiting for your plan decision. Send a new message to try again.",
          })
        toast.error(fallbackText)
        return
      }
      if ((message.type as string) === "deep_rate_limited") {
        // Track D: backend paused astream_events because Azure OpenAI
        // returned 429. It will resume from the last checkpoint after the
        // retry_in_s sleep. Surface a non-blocking toast so the user sees
        // the agent isn't frozen and knows roughly how long to wait.
        const rlMsg = message as unknown as {
          type: string
          data: { retry_in_s: number; attempt: number; max_attempts: number }
        }
        const retrySeconds = Math.max(1, Math.round(rlMsg.data.retry_in_s ?? 0))
        logger.info(
          `deep_rate_limited received (attempt=${rlMsg.data.attempt}/${rlMsg.data.max_attempts}, retry_in_s=${retrySeconds})`
        )
        toast.warning(
          i18n.t("deepExplorer.rate_limited_pause", {
            defaultValue: `Azure OpenAI rate limit hit. Resuming in ~${retrySeconds}s (attempt ${rlMsg.data.attempt}/${rlMsg.data.max_attempts}).`,
            retrySeconds,
            attempt: rlMsg.data.attempt,
            maxAttempts: rlMsg.data.max_attempts,
          }),
          { duration: Math.min(retrySeconds * 1000, 10_000) }
        )
        return
      }
      if (
        message.type === "error" &&
        typeof (message as unknown as { data?: unknown }).data === "object" &&
        (message as unknown as { data: { source?: string } }).data?.source === "deep_explorer"
      ) {
        // Track D: the backend exhausted its rate-limit retry budget (or hit
        // a non-retryable deep-explorer error). If a retry hint is attached,
        // surface it as an actionable toast so the user knows when to
        // retry instead of guessing.
        const errMsg = message as unknown as {
          type: string
          data: {
            message: string
            source: string
            retry_after_hint?: number
          }
        }
        const hint = errMsg.data.retry_after_hint
        if (typeof hint === "number" && hint > 0) {
          const waitSeconds = Math.max(1, Math.round(hint))
          logger.warn(
            `deep_explorer error with retry_after_hint=${waitSeconds}s: ${errMsg.data.message}`
          )
          toast.error(
            i18n.t("deepExplorer.rate_limit_exhausted", {
              defaultValue: `Deep Explorer paused after repeated rate limits. Try again in ~${waitSeconds}s.`,
              waitSeconds,
            }),
            { duration: 15_000 }
          )
          // Fall through: let baseOnMessage render the error bubble too.
        }
        // Intentionally fall through to the base handler so the error
        // still surfaces in the normal message stream / activity panel.
      }
      if (message.type === "artifact_emit") {
        if (!targetSid) return
        // Reattach replay guard (mirrors the deep_activity_event id-dedup
        // below). The run broadcaster replays its whole buffered window on
        // reattach without a reliable last_seen_seq for artifacts, so a single
        // tab reload mid-run re-delivers artifact_emit frames. appendArtifact
        // always pushes (artifacts-state.ts:288) — without an id guard the same
        // artifact lands twice (two is_latest=true entries in one lineage,
        // rendering the plan/deliverable twice and breaking the parent-flip on
        // the next version). New versions carry a distinct id (+ parent_id), so
        // genuine updates still append.
        const emittedArtifact = message.data as { id?: string } | undefined
        if (emittedArtifact?.id) {
          const existing = explorerStore.getState().artifactsByConversation[targetSid] ?? []
          if (existing.some((a) => a.id === emittedArtifact.id)) {
            return
          }
        }
        appendArtifact(targetSid, message.data)
        // Symmetric race-cover with the `deep_plan_proposed` handler: if a
        // plan artifact arrives AFTER the proposal frame, force focus here
        // too so the workspace doesn't stay pinned on the ask_user form.
        // The appendArtifact auto-advance already covers most cases; this
        // catches the edge where another handler reset the active id in
        // between the two frames.
        const emitted = message.data as { type?: string; id?: string } | undefined
        if (emitted && emitted.type === "plan" && emitted.id) {
          const sessionDeep = getDeepStateForSession(explorerStore.getState(), targetSid)
          if (sessionDeep.planProposed) {
            focusArtifactAction(targetSid, emitted.id)
          }
        }
        return
      }
      if (message.type === "artifact_update") {
        if (!targetSid) return
        applyArtifactPatch(targetSid, message.data.id, message.data.patch)
        return
      }
      if (message.type === "deep_activity_event") {
        if (!targetSid) return
        const activityEvent = message.data as DeepActivityEvent
        const sessionDeep = getDeepStateForSession(explorerStore.getState(), targetSid)

        // ACTIVITY-DIAG: log every tool_end on this lane so we can tell
        // whether agents stuck on RUNNING are missing tool_end frames
        // vs receiving them but pairing wrong. Tool_start is verbose so
        // we skip those; the frontend WorkerLanes pairing keys on id.
        if (activityEvent.event === "tool_end") {
          logger.info(
            `ACTIVITY-DIAG tool_end id=${activityEvent.id} tool=${activityEvent.tool} dispatch=${activityEvent.dispatch_id ?? "none"} sid=${targetSid}`
          )
        }

        // Dedup-by-id (Erik feedback 2026-05-21 — activity panel showed
        // the same "Thinking…" / "Deliverable ready" / "Reviewing answer
        // quality…" rows repeated dozens of times with identical
        // timestamps). The run broadcaster's reattach replay sends the
        // whole buffered window when a subscriber connects without a
        // ``last_seen_seq`` — combined with no FE-side dedup, even a
        // single tab reload during a live stream doubled every event
        // visible up to that point. Each backend deep_activity_event has
        // a stable ``id`` (status events use ``status-<monotonic_ns>``,
        // tool events reuse the run_id), so dedup on id is sound: if we
        // already have an event with this id in the log, skip the
        // append entirely. The trimmer below handles the cap separately.
        // Dedupe key is ``id:event`` — NOT just ``id`` — because the backend
        // reuses the tool_start event_id on the matching tool_end (streaming/
        // _legacy.py:1386-1390, "Reuse the event_id from tool_start so the
        // frontend can match them"). Keying on id alone dropped every tool_end
        // on the live-WS path: tool_start was already in the log, so the dedupe
        // saw the id as known and discarded tool_end — leaving WorkerLanes rows
        // stuck on RUNNING until the 2.5s poll backfilled them (or forever when
        // polling is skipped). The poll path already keys on id:event
        // (use-deep-live-poll.ts:83-95); match it here.
        if (activityEvent.id) {
          const seen = sessionDeep.activityLog.some(
            (e) => e.id === activityEvent.id && e.event === activityEvent.event
          )
          if (seen) {
            return
          }
        }

        // Cap activity log at 500 events to prevent unbounded memory growth
        // in long sessions. QA #SP4: when we trim, (1) insert / coalesce a
        // truncated sentinel at index 0 so ActivityLog renders the
        // "N earlier events omitted" row (the rehydration path already
        // honours the same shape at L647-650), and (2) preserve a
        // `tool_start` whose matching `tool_end` is still in the surviving
        // tail — otherwise WorkerLanes shows a lane with no label, no
        // elapsed time, no input summary because those fields live only on
        // tool_start. Mirrors the orphan-settler at L688-710.
        //
        // The "truncated" sentinel isn't in DeepActivityEvent's event union
        // (it lives in PersistedDeepActivityEvent), but ActivityLog and the
        // rehydration loop both treat the same shape at runtime — we cast
        // through `unknown` to keep the wire-shape, mirroring L650.
        const MAX_ACTIVITY_LOG = 500
        const log = sessionDeep.activityLog
        let updatedLog: DeepActivityEvent[]
        if (log.length >= MAX_ACTIVITY_LOG) {
          const dropCount = log.length - MAX_ACTIVITY_LOG + 1
          const dropped = log.slice(0, dropCount)
          const tail = log.slice(dropCount)
          const tailEndIds = new Set<string>()
          for (const e of tail) {
            if (e.event === "tool_end") tailEndIds.add(e.id)
          }
          // Retain any tool_start in the dropped slice whose tool_end is
          // still in the tail — moving them to the front of the tail
          // keeps WorkerLanes' label/elapsed/input fields intact.
          const retainedStarts: DeepActivityEvent[] = []
          let droppedNonRetained = 0
          for (const e of dropped) {
            if (e.event === "tool_start" && tailEndIds.has(e.id)) {
              retainedStarts.push(e)
            } else {
              droppedNonRetained += 1
            }
          }
          // Coalesce with an existing truncated sentinel at the head of the
          // surviving tail so we don't spam one-per-trim sentinels.
          let head: DeepActivityEvent[]
          let tailRest: DeepActivityEvent[]
          const firstTailEvent = tail[0] as unknown as
            | { event: string; dropped?: number }
            | undefined
          if (firstTailEvent && firstTailEvent.event === "truncated") {
            const prevDropped = (firstTailEvent.dropped ?? 0) + droppedNonRetained
            head = [
              {
                ...(firstTailEvent as unknown as Record<string, unknown>),
                dropped: prevDropped,
              } as unknown as DeepActivityEvent,
            ]
            tailRest = tail.slice(1)
          } else if (droppedNonRetained > 0) {
            const sentinel = {
              id: `truncated-${Date.now()}`,
              event: "truncated",
              tool: "",
              source: "deep_explorer",
              dispatch_id: null,
              timestamp: new Date().toISOString(),
              dropped: droppedNonRetained,
            } as unknown as DeepActivityEvent
            head = [sentinel]
            tailRest = tail
          } else {
            head = []
            tailRest = tail
          }
          updatedLog = [...head, ...retainedStarts, ...tailRest, activityEvent]
        } else {
          updatedLog = [...log, activityEvent]
        }

        let updatedSources = sessionDeep.sources
        const newSources = deriveSourcesFromEvent(activityEvent)
        if (newSources) {
          updatedSources = [...sessionDeep.sources, ...newSources]
        }

        replaceDeepState(targetSid, { activityLog: updatedLog, sources: updatedSources })
        return
      }
      // Terminal events arrive on the WS once the backend has already
      // written `agent_run.status` in Cosmos, so all the frontend has to
      // do is invalidate the query — the next tick the runs panel and
      // header badge reflect the new state without waiting on the
      // 30s background poll.
      if (message.type === "done" || message.type === "error") {
        // The turn is over, so any tool approval still on screen has nobody
        // waiting for its answer (#2962): drop it instead of letting a click
        // mark it approved against a waiter that is already gone. Scoped to
        // this frame's request generation: a late terminal frame from an
        // earlier socket must not clear a newer live run's cards.
        if (targetSid) cancelDeepToolApprovalsForSession(targetSid, requestGenerationOf(message))
        void queryClient.invalidateQueries({ queryKey: AGENT_RUNS_QUERY_KEY })
      }
      baseOnMessage(message, context)
    },
    [
      baseOnMessage,
      handleDeepSnapshot,
      explorerStore,
      setDeepPlanProposed,
      sendPlanDecision,
      setAskUserProposed,
      appendArtifact,
      applyArtifactPatch,
    ]
  )

  const {
    ensureSession,
    handleExampleClicked,
    handleSendQuestion,
    handleAddFiles: originalHandleAddFiles,
    handleResetFiles,
    handleCancelRequest,
    requestForceRedirect,
    handleRemoveFile: originalHandleRemoveFile,
    sendChatRequest,
    aborted,
    totalTokenCount,
    changeTokenCounts,
    handleContinue,
    getGraphFileAsFile,
  } = useChat({
    moduleRoute,
    useChatStore: () => explorerStore,
    projectId,
    getChatResponse,
    // Durable turns (issue #2465) are wired ONLY for the bare, no-`options`
    // instance of this hook — i.e. `ai-explorer-page.tsx`'s plain Explorer
    // page. `useDashChat` (and any future caller that passes `options`)
    // reaches this same shared hook with a different system prompt / tool
    // catalog and must stay on the legacy transport regardless of the
    // durable feature flag; passing `undefined` here makes that
    // structurally true, not just flag-gated off.
    ...(options
      ? {}
      : {
          resolveDurableChatTurnMode,
          buildDurableChatTurnRequest,
          onDurableChatTurnAccepted,
        }),
  })

  const routeModelDefaults = useRouteModelDefaults()
  const {
    enableHighReasoning,
    showImageReasoningWarning,
    isReasoningModelActive,
    handleImageReasoningWarningClose,
    handleImageReasoningWarningCancel,
    handleHighReasoningChange,
    handleReasoningModeChange,
    reasoningMode,
    modelOverride,
    setModelOverride,
    effortOverride,
    setEffortOverride,
    getEffectiveModelId,
    reasoningEffort,
    effectiveModel,
    selectableModels,
  } = useHighReasoning({
    files,
    handleRemoveFile: originalHandleRemoveFile,
    sessionType,
    projectDefaultModelId: activeProject?.defaultModelId,
    routeDefaultModelId: routeModelDefaults.modelId,
    routeDefaultEffort: routeModelDefaults.effort,
  })

  const prefs = researchPrefsStore((state) => state.prefs)
  const researchPrefs = prefs
  const updateFromAgentState = researchPrefsStore((state) => state.updateFromAgentState)
  const setReportFormatFile = researchPrefsStore((state) => state.setReportFormatFile)
  const setSourceUrls = researchPrefsStore((state) => state.setSourceUrls)

  const chatContainerRef = useRef<HTMLDivElement>(null)
  const previousSessionIdRef = useRef<string | null>(null)

  // Toggle off web search, M365 search, and high reasoning when switching between existing sessions
  useEffect(() => {
    const previousSessionId = previousSessionIdRef.current

    // Only toggle off when switching between two existing sessions
    // Don't toggle off when going from null (/) to a new sessionId (creating new session)
    if (previousSessionId && currentSessionId && previousSessionId !== currentSessionId) {
      // Reset to route-based defaults (same as handleStartNewChat)
      setEnableWebSearch(isWebSearchAvailable)
      setEnableM365(isM365Available && hasM365Approval)
      setEnableServiceDesk(isServiceDeskAvailable)
      setEnableNetbox(isNetboxAvailable)
      setEnableXOne(isXOneAvailable)
      setEnablePlugins(true)
      setDisabledPluginIds([])
      setEnableTreeRetrieval(false)
      setReasoningMode("auto")
      setModelOverrideStore(null)
      setEffortOverrideStore(null)
    }

    // Update the ref for next comparison
    previousSessionIdRef.current = currentSessionId
  }, [
    currentSessionId,
    setReasoningMode,
    setModelOverrideStore,
    setEffortOverrideStore,
    setEnableWebSearch,
    setEnableM365,
    setEnableServiceDesk,
    setEnableNetbox,
    setEnableXOne,
    setEnablePlugins,
    setDisabledPluginIds,
    setEnableTreeRetrieval,
    isWebSearchAvailable,
    isM365Available,
    hasM365Approval,
    isServiceDeskAvailable,
    isNetboxAvailable,
    isXOneAvailable,
  ])

  const handleAgentChange = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        const hideInfo = cookieService.get(Cookies.hideAgentModeInfo.key) === "true"
        if (!hideInfo) {
          setShowAgentModeInfo(true)
        }
      }
      setEnableAgent(enabled)
      if (!enabled) {
        setReportFormatFile(null)
      }
    },
    [setReportFormatFile]
  )

  // Deep Explorer toggle (toolbar, Cmd/Ctrl+Shift+D, the page's live-run
  // effect). A choice made while the chat is still loading its history wins over
  // the history restore (#3060), and over a runs-panel request that has not
  // settled yet. It covers the chat that is already open — opening a different
  // chat in Deep goes through `openSessionInDeepMode`.
  const handleDeepChange = useCallback(
    (enabled: boolean) => {
      deepModeRestorePendingSessionRef.current = null
      forcedDeepSessionRef.current = null
      setEnableDeep(enabled)
    },
    [setEnableDeep]
  )

  // Open another chat in Deep mode (the runs panel opening a run's workspace).
  // Keyed to that chat, because the choice happens before the navigation: the
  // chat's own restore would otherwise drop a finished or cancelled run back to
  // plain mode, hiding the workspace the user just asked for (#3060).
  const openSessionInDeepMode = useCallback(
    (sessionId: string) => {
      forcedDeepSessionRef.current = sessionId
      deepModeRestorePendingSessionRef.current = null
      setEnableDeep(true)
    },
    [setEnableDeep]
  )

  const hasLatestPlan = useMemo(() => {
    const allAnswers = chatState.streamingAnswer
      ? [...chatState.messages, chatState.streamingAnswer]
      : chatState.messages
    const latestAnswer = allAnswers.at(-1)?.[1]
    if (!latestAnswer?.context) return false

    const agentState = latestAnswer.context.agent_state as ScholarState | undefined
    if (!agentState) return false

    const latestPlan = agentState.latest_plan
    return typeof latestPlan === "string" && latestPlan.trim().length > 0
  }, [chatState.messages, chatState.streamingAnswer])

  useEffect(() => {
    const allAnswers = chatState.streamingAnswer
      ? [...chatState.messages, chatState.streamingAnswer]
      : chatState.messages
    const latestAnswer = allAnswers.at(-1)?.[1]
    if (latestAnswer?.context) {
      const agentState = latestAnswer.context.agent_state as ScholarState | undefined
      if (agentState) {
        updateFromAgentState(agentState)
      }
    }
  }, [chatState.messages, chatState.streamingAnswer])

  // Skip Azure AI Search query when Responses API file_search is enabled
  // (files are in Azure OpenAI vector stores, not Azure AI Search)
  // Wait for config to load before enabling query to prevent premature execution
  const indexedFilesQuery = useIndexedFilesQuery({
    sessionId: currentSessionId ?? undefined,
    token: settingsService.token,
    projectId,
    enabled: config !== undefined && !useResponsesFileSearch,
  })

  // Sync indexed files from server, combined with session file refs
  useEffect(() => {
    // If no session, clear once
    if (!currentSessionId) {
      if (filesInIndex.length > 0) setFilesInIndex([])
      return
    }

    // When using Responses API file_search, ignore Azure AI Search query data
    // (it may contain stale cached data from before Responses API was enabled)
    const queryData = useResponsesFileSearch ? [] : (indexedFilesQuery.data ?? [])
    const combinedSet = new Set([...queryData, ...sessionFileRefs])
    const data = Array.from(combinedSet)

    // Compare unordered to avoid thrashing on ordering differences
    const isSame =
      filesInIndex.length === data.length &&
      (() => {
        const setA = new Set(filesInIndex)
        for (const item of data) {
          if (!setA.has(item)) return false
        }
        return true
      })()

    if (!isSame) {
      setFilesInIndex(data)
      return
    }

    if (indexedFilesQuery.isError && filesInIndex.length > 0 && sessionFileRefs.length === 0) {
      setFilesInIndex([])
    }
  }, [
    currentSessionId,
    indexedFilesQuery.data,
    indexedFilesQuery.isError,
    sessionFileRefs,
    useResponsesFileSearch,
  ])

  const handleStartResearch = async () => {
    if (!hasLatestPlan) return

    try {
      await handleSendQuestion({
        question: "[start]",
        startResearch: true,
      })
    } catch (error) {
      logger.error("Error starting research:", error as Error)
    }
  }

  // --- Response timing ---
  const startResponseTimer = explorerStore((state) => state.startResponseTimer)
  const markFirstToken = explorerStore((state) => state.markFirstToken)
  const markResponseEnd = explorerStore((state) => state.markResponseEnd)

  // Mark first visible token when streamingAnswer first gets content or thinking.
  const streamingContent = chatState.streamingAnswer?.[1]?.content
  const streamingThinking = chatState.streamingAnswer?.[1]?.thinking
  const firstVisibleStreamingText = streamingContent || streamingThinking
  const prevFirstVisibleStreamingTextRef = useRef<string | undefined>(undefined)

  // Reset the ref when a new request starts (isStreaming flips to true)
  useEffect(() => {
    if (chatState.isStreaming) {
      prevFirstVisibleStreamingTextRef.current = undefined
    }
  }, [chatState.isStreaming])

  useEffect(() => {
    if (
      firstVisibleStreamingText &&
      firstVisibleStreamingText.length > 0 &&
      (prevFirstVisibleStreamingTextRef.current === undefined ||
        prevFirstVisibleStreamingTextRef.current.length === 0)
    ) {
      markFirstToken()
    }
    prevFirstVisibleStreamingTextRef.current = firstVisibleStreamingText
  }, [firstVisibleStreamingText, markFirstToken])

  // Mark response end when streaming stops, extracting token counts from the last message
  const wasStreamingRef = useRef(false)
  useEffect(() => {
    if (wasStreamingRef.current && !chatState.isStreaming) {
      // Extract token counts from the last completed message's usage_metadata
      let tokenInfo: { outputTokens?: number; reasoningTokens?: number } | undefined
      const lastMsg = chatState.messages[chatState.messages.length - 1]
      if (lastMsg) {
        const ctx = lastMsg[1]?.context as Record<string, unknown> | undefined
        const usageMeta = ctx?.["usage_metadata"] as Record<string, unknown> | undefined
        if (usageMeta) {
          const outputTokens =
            typeof usageMeta["output_tokens"] === "number" ? usageMeta["output_tokens"] : undefined
          const reasoningTokens =
            typeof usageMeta["reasoning_tokens"] === "number"
              ? usageMeta["reasoning_tokens"]
              : undefined
          if (outputTokens !== undefined || reasoningTokens !== undefined) {
            tokenInfo = { outputTokens, reasoningTokens }
          }
        }
      }
      markResponseEnd(tokenInfo)
    }
    wasStreamingRef.current = chatState.isStreaming
  }, [chatState.isStreaming, chatState.messages, markResponseEnd])

  async function getChatResponse({
    question,
    abortController,
    isContinuation,
    isRetry = false,
    startResearch = false,
    agentId,
    forceWebSearch,
    files: filesForRequest,
    userInputData,
    requestToken,
  }: {
    question: string
    abortController: AbortController
    isContinuation?: boolean
    isRetry?: boolean
    startResearch?: boolean
    agentId?: string
    forceWebSearch?: boolean
    files?: File[]
    /**
     * Text START-field values (key → string) for a workflow run, forwarded into
     * context.overrides.user_input_data so the backend seeds context.user_input_data
     * even on the file-upload chat path (files ride the upload pipeline separately).
     */
    userInputData?: Record<string, string>
    requestToken: symbol
  }) {
    const effectiveFiles = filesForRequest ?? files
    if (!explorerStore.getState().responseTiming) {
      explorerStore.getState().startResponseTimer()
    }

    // IMPORTANT: Capture session ID once at the start and use it throughout.
    // Don't re-read from ChatService later as useSessionSync may clear it
    // during async operations (race condition with navigation).
    const capturedSessionId = chatService.getOpenSession(moduleSessionType)?.id

    if (!capturedSessionId) {
      logger.error("No session ID available when trying to send chat request")
      throw new Error("No active session. Please try again.")
    }
    const translationRequest = translationForRequest(capturedSessionId, isRetry)
    const requestGenerationId = getChatRequestGenerationId(explorerStore, capturedSessionId)
    if (!requestGenerationId) {
      throw new Error("Chat request generation is missing. Please try again.")
    }
    // Record the turn's mode before the first await, so a fast history load of
    // this new chat can't settle the Deep restore before the entry exists
    // (#3347). The transport choice below refreshes it with the final mode.
    sentTurnModeRef.current.set(capturedSessionId, {
      deep: !startResearch && explorerStore.getState().enableDeep,
      requestGenerationId,
    })

    // Read the current web search and M365 state from the store (not the closure)
    const currentEnableWebSearch = forceWebSearch ?? explorerStore.getState().enableWebSearch
    const currentEnableM365 = explorerStore.getState().enableM365
    const currentEnableServiceDesk = explorerStore.getState().enableServiceDesk
    const currentEnableNetbox = explorerStore.getState().enableNetbox
    const currentEnableXOne = explorerStore.getState().enableXOne
    const pluginState = explorerStore.getState()
    const pluginOverrides = buildPluginRequestOverrides({
      available: isPluginsAvailable,
      enabled: pluginState.enablePlugins,
      installedPluginIds: pluginState.installedPluginIds,
      disabledPluginIds: pluginState.disabledPluginIds,
    })
    // Personal search is on unless the owner switched it off for this chat (PS-5); the server
    // combines this with the choice it stores, and only an explicit off is ever sent.
    const personalSearchOverrides = pluginState.personalSearchEnabled
      ? {}
      : { use_personal_search: false }
    const effectiveEnableWebSearch = isWebSearchAvailable ? currentEnableWebSearch : false
    // Block sends while M365 approval has not loaded yet. Reading
    // `hasM365Approval` during this window would silently force the message
    // through without M365 access for approved users (the store default is
    // false; `hasInitializedDefaults` only flips once approval settles). The
    // loading window is brief (~ first 200-500 ms after mount), so a thrown
    // error here is preferable to data going out without the user's tools.
    // A background refetch of already-loaded settings does not block (#3323
    // E10): it made the first send after New chat fail.
    if (isM365Available && isM365ApprovalUnresolved) {
      throw new Error("Microsoft 365 approval state is still loading — please retry.")
    }
    const effectiveEnableM365 = isM365Available && hasM365Approval ? currentEnableM365 : false
    const effectiveEnableServiceDesk = isServiceDeskAvailable ? currentEnableServiceDesk : false
    const effectiveEnableNetbox = isNetboxAvailable ? currentEnableNetbox : false
    const effectiveEnableXOne = isXOneAvailable ? currentEnableXOne : false
    const allAnswers = chatState.streamingAnswer
      ? [...chatState.messages, chatState.streamingAnswer]
      : chatState.messages
    const messages: ResponseMessage[] = allAnswers.flatMap((a) => [
      { content: a[0], role: "user" },
      // `assistant_files` rides along so a hosted agent's next turn knows the
      // names of the files it already delivered (D5); the engine folds them
      // into the projected history text and nothing else consumes them.
      {
        assistant_files: getAssistantFiles(a[1]),
        content: getResponseContent(a[1]),
        role: "assistant",
      },
    ])
    const activeSettings = await settingsService.getActiveSettings()
    const modelToUse = getEffectiveModelId()

    let reportFormatPayload: string | { filename: string; content: number[] } | undefined
    const reportFormatSourcePayload: "file" | "message" | "standard" | "structured_summary" =
      researchPrefs.reportFormatSource

    if (enableAgent) {
      if (researchPrefs.reportFormatSource === "file" && researchPrefs.reportFormatFile) {
        const fileBuffer = await researchPrefs.reportFormatFile.arrayBuffer()
        reportFormatPayload = {
          filename: researchPrefs.reportFormatFile.name,
          content: Array.from(new Uint8Array(fileBuffer)),
        }
      } else if (researchPrefs.reportFormatSource === "message") {
        reportFormatPayload = researchPrefs.reportFormatText
      } else if (researchPrefs.reportFormatSource === "standard") {
        reportFormatPayload = "APA"
      }
    }

    /**
     * Determine which libraries to search.
     *
     * Restricted variants: Use configured libraries only (replaces user selection).
     * Normal variants: Use user's enabled library selections.
     *
     * Note: Library-level security (OIDs/groups) applies regardless of this setting.
     */
    let searchServices: string[]
    if (overrides?.restrictToLibraryId && typeof overrides.restrictToLibraryId === "string") {
      // Single library restriction - replace user selection
      searchServices = [overrides.restrictToLibraryId]
    } else if (overrides?.restrictToLibraryIds && Array.isArray(overrides.restrictToLibraryIds)) {
      // Multiple library restriction - replace user selection
      searchServices = overrides.restrictToLibraryIds as string[]
    } else {
      // Normal case - use user's enabled libraries from store
      searchServices = Object.entries(enabledLibraries)
        .filter(([, library]) => library.checked)
        .map(([libraryId]) => libraryId)
    }

    // Build overrides payload explicitly to satisfy typing
    let agentOverrides = enableAgent
      ? (buildAgentOverrides(
          researchPrefs,
          reportFormatPayload,
          reportFormatSourcePayload
        ) as Partial<ChatAppRequestOverrides>)
      : undefined

    // Defense-in-depth: strip "web" from research sources when web search is disabled by route override
    if (agentOverrides && !effectiveEnableWebSearch) {
      const filteredSources = agentOverrides.sources?.filter((s) => s !== "web")
      agentOverrides = {
        ...agentOverrides,
        sources: filteredSources?.length ? filteredSources : ["company_libraries"],
      }
    }

    const trustedSourceUrls = effectiveEnableWebSearch
      ? processSourceUrls(researchPrefs.sourceUrls)
      : undefined
    const trustedMode = effectiveEnableWebSearch ? researchPrefs.trustedMode : undefined

    type ChatAppRequestOverridesWithActiveDocument = ChatAppRequestOverrides & {
      active_document_id?: string
      active_document_title?: string
      active_document_content_snippet?: string
      active_selection_text?: string
      active_selection_start?: number
      active_selection_end?: number
      document_ids?: string[]
      route_id?: string
      request_generation_id?: string
      deep_narration?: boolean
      deep_fact_check?: boolean
    }

    // Effort precedence: see resolveRequestedReasoningEffort's own doc for
    // the full (1)-(4) chain, shared with buildDurableChatTurnRequest below.
    const requestedReasoningEffort = resolveRequestedReasoningEffort({
      effortOverride,
      reasoningMode,
      reasoningEffort,
      effectiveModelId: effectiveModel?.id,
      effectiveModelDefaultEffort: effectiveModel?.default_effort,
      question,
      fileCount: effectiveFiles.length,
      locale: i18n.language,
    })

    const reasoningEffortForRequest = resolveReasoningEffortForRequest(
      requestedReasoningEffort,
      effectiveModel?.supported_efforts,
      effectiveModel?.default_effort
    )

    const overridesPayload: ChatAppRequestOverridesWithActiveDocument = {
      exclude_category: undefined,
      ...(overrides?.showCategoryPicker &&
      overrides?.restrictToLibraryId &&
      typeof overrides.restrictToLibraryId === "string"
        ? { include_categories: variantSettings?.selected_categories ?? [] }
        : {}),
      top: settings.retrieveCount,
      retrieval_mode: activeSettings.chatRagRetrievalMode.mode,
      semantic_ranker: activeSettings.chatRagSemanticRanking,
      semantic_captions: activeSettings.chatRagContextSummary,
      suggest_followup_questions: suggestFollowupQuestions,
      use_oid_security_filter: false,
      use_groups_security_filter: false,
      vector_fields: [VectorFieldOptions.Embedding],
      use_custom_gpt: true,
      custom_gpt_model: modelToUse,
      session_id: capturedSessionId,
      request_generation_id: requestGenerationId,
      temperature: settings.temperature,
      use_gpt4v: false,
      gpt4v_input: GPT4VInput.Texts,
      search_services: searchServices,
      use_web_search: effectiveEnableWebSearch,
      use_m365: effectiveEnableM365,
      use_service_desk: effectiveEnableServiceDesk,
      use_netbox: effectiveEnableNetbox,
      use_x_one: effectiveEnableXOne,
      ...pluginOverrides,
      ...personalSearchOverrides,
      is_continuation: isContinuation,
      reasoning_effort: reasoningEffortForRequest,
      use_agent_mode: enableAgent,
      deep_narration: enableNarration,
      deep_fact_check: enableFactCheck,
      enable_tree_retrieval: enableTreeRetrieval,
      start_research: startResearch,
      workflow_id: agentId,
      document_ids:
        selectedDocuments.length > 0 ? selectedDocuments.map((document) => document.id) : undefined,
      active_document_id: isCanvasOpen ? (activeCanvasDocumentId ?? undefined) : undefined,
      active_document_can_edit:
        isCanvasOpen && activeCanvasDocumentId ? activeCanvasDocumentCanEdit : undefined,
      active_document_title: isCanvasOpen ? activeCanvasDocumentTitle || undefined : undefined,
      active_document_content_snippet: isCanvasOpen
        ? activeCanvasDocumentContent.slice(0, 4000)
        : undefined,
      active_selection_text:
        isCanvasOpen && activeCanvasSelectionText.trim().length > 0
          ? activeCanvasSelectionText.slice(0, 2000)
          : undefined,
      active_selection_start:
        isCanvasOpen && activeCanvasSelectionText.trim().length > 0
          ? (activeCanvasSelectionStart ?? undefined)
          : undefined,
      active_selection_end:
        isCanvasOpen && activeCanvasSelectionText.trim().length > 0
          ? (activeCanvasSelectionEnd ?? undefined)
          : undefined,
      source_urls: trustedSourceUrls,
      trusted_mode: trustedMode,
      // Signal to backend that the chat can use indexed files
      has_indexed_files: filesInIndex.length > 0,
      // Hint backend to include project aggregator in search filter
      ...(projectId ? { extra_session_ids: [`project-${projectId}`] } : {}),
      language: (() => {
        const langCode = i18n.language.split("-")[0]
        const languageMap: Record<string, string> = {
          en: "english",
          sv: "swedish",
          fi: "finnish",
          no: "norwegian",
          de: "german",
        }
        return languageMap[langCode] ?? "english"
      })(),
      active_citations: activeCitations,
      ...(options?.agentName ? { agent_name: options.agentName } : {}),
      ...(options?.getExtraOverrides?.(explorerStore) ?? {}),
      ...(agentOverrides ?? {}),
      ...(overrides as Partial<ChatAppRequestOverridesWithActiveDocument>),
      route_id: moduleRouteId,
      ...translatorOverridesForSource(translationRequest, {
        fileCount: effectiveFiles.length,
        documentCount: selectedDocuments.length,
        sessionFiles: sessionFileRefs,
        indexedFileCount: filesInIndex.length,
      }),
    }

    const request: ChatAppRequest = {
      messages: [...messages, { content: question, role: "user" }],
      stream: shouldStream && (gptModel?.feature_flags?.supports_streaming ?? true),
      context: {
        overrides: {
          ...overridesPayload,
          ...(usePromptTemplateForNextMessage && {
            prompt_template_prefix: "prompt_library",
            prompt_id: selectedPromptId ?? undefined,
            prompt_title: selectedPromptTitle ?? undefined,
          }),
          // Seed start-field text values so a workflow run started from chat WITH
          // files still receives them (the no-file /run path carries these already).
          ...(userInputData && Object.keys(userInputData).length > 0
            ? { user_input_data: userInputData }
            : {}),
        },
      },
      session_state: chatState.messages.length
        ? getSessionState(chatState.messages[chatState.messages.length - 1][1])
        : null,
    }

    if (usePromptTemplateForNextMessage) {
      setUsePromptTemplateForNextMessage(false)
      clearSelectedPrompt()
    }

    explorerStore.getState().setTranslationRequest(null)

    try {
      // Use the captured session ID consistently for request context - this ensures the response
      // is associated with the session that initiated the request, even if navigation occurs.
      const sessionIdForRequest = capturedSessionId ?? null
      // For isActive checks during streaming, we intentionally use current state (not captured).
      // This detects if the user switched sessions, allowing us to suppress UI updates for
      // responses belonging to a session that's no longer active.
      const getCurrentSessionId = () => chatService.getOpenSession(moduleSessionType)?.id ?? null
      const markWsOpenIfActive = () => {
        if (getCurrentSessionId() === sessionIdForRequest) {
          explorerStore.getState().markWsOpen()
        }
      }

      if (startResearch && !overridesPayload.use_translator) {
        logger.info("Starting Research")
        return await startResearchApi(
          request,
          effectiveFiles,
          settingsService.token,
          abortController.signal,
          (message) => {
            // Always pass sessionId in context - handler will update correct session
            const context = {
              sessionId: sessionIdForRequest,
              isActive: getCurrentSessionId() === sessionIdForRequest,
              requestToken,
            }
            onMessage(message, context)
          },
          markWsOpenIfActive
        )
      }

      // Deep Explorer takes precedence over Agent when both toggles are on.
      // Phase 1 walking skeleton: streams through the existing activity channel.
      // Read fresh from the store: a `/deep` prefix on the same keystroke that
      // submits the question flips `enableDeep` just before this line, and the
      // closure copy would still be `false`.
      const currentEnableDeep = explorerStore.getState().enableDeep
      if (sessionIdForRequest) {
        sentTurnModeRef.current.set(sessionIdForRequest, {
          deep: currentEnableDeep,
          requestGenerationId,
        })
      }
      if (currentEnableDeep) {
        // Capture this socket's own send fn so the close handler only clears
        // the map entry if it still points at THIS socket. A reattach WS (or a
        // newer foreground turn) can re-register under the same sessionId while
        // this socket is still open (onClientReady fires synchronously); an
        // unconditional delete on this socket's late close would then clobber
        // the live socket's control channel, breaking Approve/Reject/ask-user.
        // Mirrors unregisterSessionWebSocket's identity check.
        let registeredSend: ((message: Record<string, unknown>) => void) | null = null
        return await explorerDeepChatWebSocketApi(
          request,
          effectiveFiles,
          settingsService.token,
          abortController.signal,
          (message) => {
            const context = {
              sessionId: sessionIdForRequest,
              isActive: getCurrentSessionId() === sessionIdForRequest,
              requestToken,
              requestGenerationId,
            }
            onMessage(message, context)
          },
          (send) => {
            if (sessionIdForRequest) {
              registeredSend = send
              registerDeepSocketSender(sessionIdForRequest, send)
            }
          },
          () => {
            if (sessionIdForRequest && registeredSend) {
              unregisterDeepSocketSender(sessionIdForRequest, registeredSend)
            }
          },
          markWsOpenIfActive
        )
      }

      if (enableAgent && !overridesPayload.use_translator) {
        return await scholarChatWebSocketApi(
          request,
          effectiveFiles,
          settingsService.token,
          abortController.signal,
          (message) => {
            // Always pass sessionId in context - handler will update correct session
            const context = {
              sessionId: sessionIdForRequest,
              isActive: getCurrentSessionId() === sessionIdForRequest,
              requestToken,
            }
            onMessage(message, context)
          },
          markWsOpenIfActive
        )
      }

      return await explorerChatWebSocketApi(
        request,
        effectiveFiles,
        settingsService.token,
        abortController.signal,
        (message) => {
          // Always pass sessionId in context so handler knows which session it's for
          const context = {
            sessionId: sessionIdForRequest,
            isActive: getCurrentSessionId() === sessionIdForRequest,
            requestToken,
          }
          onMessage(message, context)
        },
        moduleRouteId,
        sessionIdForRequest,
        markWsOpenIfActive
      )
    } catch (error) {
      logger.warn(
        "WebSocket connection failed, falling back to HTTP",
        error instanceof Error ? error : new Error(String(error))
      )
      return Response.json({
        error: "WebSocket connection failed",
      })
    }
  }

  /**
   * Durable turns (issue #2465), plain Explorer only — see the `!options`
   * gate on the `useChat({...})` call above. `enableDeep` is re-read fresh
   * from the store (not the closure) for the same reason `getChatResponse`
   * does above: a `/deep` prefix on the same keystroke that submits the
   * question can flip it just before this runs.
   */
  function resolveDurableChatTurnMode({
    startResearch,
  }: {
    startResearch?: boolean
  }): DurableChatTurnMode | null {
    return getDurableChatTurnMode({
      isDeepExplorer: explorerStore.getState().enableDeep,
      isTranslator: false,
      startResearch: startResearch ?? false,
      enableAgent,
    })
  }

  /**
   * Durable-turn HTTP acceptance payload for plain Explorer (issue #2465).
   * Deliberately NOT an extraction/refactor of `getChatResponse`'s override
   * payload above — that function is the legacy path and must stay
   * byte-for-byte unchanged (`use-chat.ts`'s gate only ever calls this
   * builder, never `getChatResponse`, for a durable send). This mirrors the
   * overrides that matter for plain Explorer's retrieval/model/search-surface
   * behavior; it omits Deep/Agent/canvas/prompt-template overrides that can
   * never apply here — this builder is only reachable when
   * `resolveDurableChatTurnMode` resolved `"explorer"`, i.e. none of those
   * modes are active (see the gate in `use-chat.ts`'s `sendChatRequest`).
   *
   * This payload has been exercised live on a running backend as part of
   * this branch's own QA (accept → stream → reload mid-turn → reattach;
   * long web-search answers settling cleanly) — see the PR description's
   * verification section for the full list of what was and wasn't covered.
   */
  async function buildDurableChatTurnRequest({
    isRetry,
    forceWebSearch,
    question,
    requestFiles,
    turnId,
    userInputData,
  }: {
    forceWebSearch?: boolean
    isRetry: boolean
    question: string
    requestFiles: File[] | undefined
    turnId: string
    userInputData?: Record<string, string>
  }): Promise<ChatAppRequest> {
    const effectiveFiles = requestFiles ?? files
    const capturedSessionId = chatService.getOpenSession(moduleSessionType)?.id
    if (!capturedSessionId) {
      throw new Error("No active session. Please try again.")
    }
    const translationRequest = translationForRequest(capturedSessionId, isRetry)

    // Same M365-approval guard as getChatResponse: never send an
    // M365-eligible request before approval state has loaded.
    if (isM365Available && isM365ApprovalUnresolved) {
      throw new Error("Microsoft 365 approval state is still loading — please retry.")
    }

    const currentEnableWebSearch = forceWebSearch ?? explorerStore.getState().enableWebSearch
    const currentEnableM365 = explorerStore.getState().enableM365
    const currentEnableServiceDesk = explorerStore.getState().enableServiceDesk
    const currentEnableNetbox = explorerStore.getState().enableNetbox
    const currentEnableXOne = explorerStore.getState().enableXOne
    const pluginState = explorerStore.getState()
    const pluginOverrides = buildPluginRequestOverrides({
      available: isPluginsAvailable,
      enabled: pluginState.enablePlugins,
      installedPluginIds: pluginState.installedPluginIds,
      disabledPluginIds: pluginState.disabledPluginIds,
    })
    // Personal search is on unless the owner switched it off for this chat (PS-5); the server
    // combines this with the choice it stores, and only an explicit off is ever sent.
    const personalSearchOverrides = pluginState.personalSearchEnabled
      ? {}
      : { use_personal_search: false }
    const effectiveEnableWebSearch = isWebSearchAvailable ? currentEnableWebSearch : false
    const effectiveEnableM365 = isM365Available && hasM365Approval ? currentEnableM365 : false
    const effectiveEnableServiceDesk = isServiceDeskAvailable ? currentEnableServiceDesk : false
    const effectiveEnableNetbox = isNetboxAvailable ? currentEnableNetbox : false
    const effectiveEnableXOne = isXOneAvailable ? currentEnableXOne : false

    const allAnswers = chatState.streamingAnswer
      ? [...chatState.messages, chatState.streamingAnswer]
      : chatState.messages
    const messages: ResponseMessage[] = allAnswers.flatMap((a) => [
      { content: a[0], role: "user" },
      // `assistant_files` rides along so a hosted agent's next turn knows the
      // names of the files it already delivered (D5); the engine folds them
      // into the projected history text and nothing else consumes them.
      {
        assistant_files: getAssistantFiles(a[1]),
        content: getResponseContent(a[1]),
        role: "assistant",
      },
    ])

    const activeSettings = await settingsService.getActiveSettings()
    const modelToUse = getEffectiveModelId()

    let searchServices: string[]
    if (overrides?.restrictToLibraryId && typeof overrides.restrictToLibraryId === "string") {
      searchServices = [overrides.restrictToLibraryId]
    } else if (overrides?.restrictToLibraryIds && Array.isArray(overrides.restrictToLibraryIds)) {
      searchServices = overrides.restrictToLibraryIds as string[]
    } else {
      searchServices = Object.entries(enabledLibraries)
        .filter(([, library]) => library.checked)
        .map(([libraryId]) => libraryId)
    }

    // Same formula as getChatResponse's own trustedSourceUrls/trustedMode
    // (issue #2513 finding): both feed the SAME `ExplorerApproach.run` the
    // legacy transport calls — the approach has no notion of which transport
    // invoked it, so dropping these here doesn't skip Canvas/trust logic
    // server-side, it just never tells the server the user restricted to
    // trusted sources. That silently turned "Only trusted sources" into
    // unrestricted web search over the durable transport.
    const trustedSourceUrls = effectiveEnableWebSearch
      ? processSourceUrls(researchPrefs.sourceUrls)
      : undefined
    const trustedMode = effectiveEnableWebSearch ? researchPrefs.trustedMode : undefined

    // Same effort resolution (and the same #2513 supported-efforts clamp
    // below) as getChatResponse above — see resolveRequestedReasoningEffort's
    // doc for the full precedence chain shared by both builders.
    const requestedReasoningEffort = resolveRequestedReasoningEffort({
      effortOverride,
      reasoningMode,
      reasoningEffort,
      effectiveModelId: effectiveModel?.id,
      effectiveModelDefaultEffort: effectiveModel?.default_effort,
      question,
      fileCount: effectiveFiles.length,
      locale: i18n.language,
    })
    const reasoningEffortForRequest = resolveReasoningEffortForRequest(
      requestedReasoningEffort,
      effectiveModel?.supported_efforts,
      effectiveModel?.default_effort
    )

    const overridesPayload: ChatAppRequestOverrides & Record<string, unknown> = {
      ...(overrides?.showCategoryPicker &&
      overrides?.restrictToLibraryId &&
      typeof overrides.restrictToLibraryId === "string"
        ? { include_categories: variantSettings?.selected_categories ?? [] }
        : {}),
      top: settings.retrieveCount,
      retrieval_mode: activeSettings.chatRagRetrievalMode.mode,
      semantic_ranker: activeSettings.chatRagSemanticRanking,
      semantic_captions: activeSettings.chatRagContextSummary,
      suggest_followup_questions: suggestFollowupQuestions,
      use_oid_security_filter: false,
      use_groups_security_filter: false,
      vector_fields: [VectorFieldOptions.Embedding],
      use_custom_gpt: true,
      custom_gpt_model: modelToUse,
      session_id: capturedSessionId,
      temperature: settings.temperature,
      use_gpt4v: false,
      gpt4v_input: GPT4VInput.Texts,
      search_services: searchServices,
      use_web_search: effectiveEnableWebSearch,
      use_m365: effectiveEnableM365,
      use_service_desk: effectiveEnableServiceDesk,
      use_netbox: effectiveEnableNetbox,
      use_x_one: effectiveEnableXOne,
      ...pluginOverrides,
      ...personalSearchOverrides,
      is_continuation: false,
      reasoning_effort: reasoningEffortForRequest,
      use_agent_mode: false,
      deep_narration: false,
      deep_fact_check: false,
      enable_tree_retrieval: enableTreeRetrieval,
      start_research: false,
      document_ids:
        selectedDocuments.length > 0 ? selectedDocuments.map((document) => document.id) : undefined,
      // Canvas context (issue #2513 finding): same fields getChatResponse
      // sends, computed the same way. Without these, generating a durable
      // answer while Canvas is open silently loses the "edit this document"/
      // selection context the legacy transport already carries.
      active_document_id: isCanvasOpen ? (activeCanvasDocumentId ?? undefined) : undefined,
      active_document_can_edit:
        isCanvasOpen && activeCanvasDocumentId ? activeCanvasDocumentCanEdit : undefined,
      active_document_title: isCanvasOpen ? activeCanvasDocumentTitle || undefined : undefined,
      active_document_content_snippet: isCanvasOpen
        ? activeCanvasDocumentContent.slice(0, 4000)
        : undefined,
      active_selection_text:
        isCanvasOpen && activeCanvasSelectionText.trim().length > 0
          ? activeCanvasSelectionText.slice(0, 2000)
          : undefined,
      active_selection_start:
        isCanvasOpen && activeCanvasSelectionText.trim().length > 0
          ? (activeCanvasSelectionStart ?? undefined)
          : undefined,
      active_selection_end:
        isCanvasOpen && activeCanvasSelectionText.trim().length > 0
          ? (activeCanvasSelectionEnd ?? undefined)
          : undefined,
      source_urls: trustedSourceUrls,
      trusted_mode: trustedMode,
      has_indexed_files: filesInIndex.length > 0,
      ...(projectId ? { extra_session_ids: [`project-${projectId}`] } : {}),
      language: (() => {
        const langCode = i18n.language.split("-")[0]
        const languageMap: Record<string, string> = {
          en: "english",
          sv: "swedish",
          fi: "finnish",
          no: "norwegian",
          de: "german",
        }
        return languageMap[langCode] ?? "english"
      })(),
      active_citations: activeCitations,
      route_id: moduleRouteId,
      ...translatorOverridesForSource(translationRequest, {
        fileCount: effectiveFiles.length,
        documentCount: selectedDocuments.length,
        sessionFiles: sessionFileRefs,
        indexedFileCount: filesInIndex.length,
      }),
    }

    // Prompt-library one-shot (issue #2513 finding): same fields and the same
    // clear-after-use cleanup getChatResponse performs, so selecting a prompt
    // template behaves identically regardless of which transport the send
    // resolves to.
    const promptTemplateOverrides = usePromptTemplateForNextMessage
      ? {
          prompt_template_prefix: "prompt_library",
          prompt_id: selectedPromptId ?? undefined,
          prompt_title: selectedPromptTitle ?? undefined,
        }
      : {}
    if (usePromptTemplateForNextMessage) {
      setUsePromptTemplateForNextMessage(false)
      clearSelectedPrompt()
    }

    const request: ChatAppRequest = {
      turn_id: turnId,
      messages: [...messages, { content: question, role: "user" }],
      stream: shouldStream && (gptModel?.feature_flags?.supports_streaming ?? true),
      context: {
        overrides: {
          ...overridesPayload,
          ...promptTemplateOverrides,
          ...(userInputData && Object.keys(userInputData).length > 0
            ? { user_input_data: userInputData }
            : {}),
        },
      },
      session_state: chatState.messages.length
        ? getSessionState(chatState.messages[chatState.messages.length - 1][1])
        : null,
    }
    pendingTranslationByRequestRef.current.set(request, translationRequest)
    return request
  }

  const handleWebSearchChange = (enabled: boolean) => {
    setEnableWebSearch(enabled)
    // Reset trusted sources whenever web search state changes
    setSourceUrls("")
  }

  const handleTreeRetrievalChange = useCallback(
    (enabled: boolean) => {
      setEnableTreeRetrieval(enabled)
    },
    [setEnableTreeRetrieval]
  )

  const handleM365Change = useCallback(
    (enabled: boolean) => {
      if (enabled && !hasM365Approval) {
        // Declined users have made a persistent "off" choice — direct them to
        // Preferences instead of re-showing the consent modal inline. Keeps the
        // settings panel canonical and respects their opt-out.
        if (hasDeclinedM365) {
          toast.info(
            i18n.t("m365_tool.toast.declined_in_settings", {
              defaultValue: "Microsoft 365 access is off — turn it on in Preferences.",
            })
          )
          openPreferencesWith("general")
          return
        }
        setShowM365Info(true)
        return
      }
      setEnableM365(enabled)
    },
    [hasM365Approval, hasDeclinedM365, openPreferencesWith, setEnableM365]
  )

  const handleM365Approve = useCallback(() => {
    approveM365()
    setEnableM365(true)
    setShowM365Info(false)
  }, [approveM365, setEnableM365])

  const handleM365Decline = useCallback(() => {
    declineM365()
    setEnableM365(false)
    setShowM365Info(false)
  }, [declineM365, setEnableM365])

  const handleM365Dismiss = useCallback(() => {
    // Non-persistent dismiss (overlay click / Esc). Flip the composer toggle
    // off for this session but don't record a decision — user gets re-prompted
    // next time they try to enable.
    setEnableM365(false)
    setShowM365Info(false)
  }, [setEnableM365])

  const handleServiceDeskChange = useCallback(
    (enabled: boolean) => {
      setEnableServiceDesk(enabled)
    },
    [setEnableServiceDesk]
  )

  const handleNetboxChange = useCallback(
    (enabled: boolean) => {
      setEnableNetbox(enabled)
    },
    [setEnableNetbox]
  )

  const handleXOneChange = useCallback(
    (enabled: boolean) => {
      setEnableXOne(enabled)
    },
    [setEnableXOne]
  )

  const handlePluginsChange = useCallback(
    (enabled: boolean) => {
      setEnablePlugins(enabled)
    },
    [setEnablePlugins]
  )

  const handlePluginDisabledChange = useCallback(
    (pluginId: string, disabled: boolean) => {
      setPluginDisabled(pluginId, disabled)
    },
    [setPluginDisabled]
  )

  const handleInstalledPluginsChange = useCallback(
    (pluginIds: string[]) => {
      setInstalledPluginIds(pluginIds)
    },
    [setInstalledPluginIds]
  )

  const handleDocumentsSelected = useCallback(
    (documents: UserDocument[]) => {
      documents.forEach((document) => addDocument(document))
    },
    [addDocument]
  )

  const handleRemoveDocument = useCallback(
    (documentId: string) => {
      removeDocument(documentId)
    },
    [removeDocument]
  )

  const handleAddFiles = (newFiles: File[]) => {
    if (newFiles.length < 1) return

    originalHandleAddFiles(newFiles)
  }

  const handleRemoveFile = async (file: File) => {
    originalHandleRemoveFile(file)
    try {
      const result = await indexedFilesQuery.refetch()
      const latestIndexed = result.data ?? filesInIndex
      if (Array.isArray(latestIndexed)) {
        setFilesInIndex(latestIndexed)
      }
      if (latestIndexed.includes(file.name)) {
        await handleRemoveIndexedFile(file.name)
      }
    } catch (e) {
      logger.error("Failed to refetch indexed files before removal", e as Error)
    }
  }

  async function handleGraphFileSelected(item: GraphFileItem) {
    try {
      const file = await getGraphFileAsFile(item)
      originalHandleAddFiles([file])
    } catch (e) {
      logger.error("Graph file download failed", e as Error)
      // The backend rejects known-unreadable files (e.g. an encrypted/
      // protected Office file — see graph_download_file's OLE2/CFBF check)
      // with a translatable message instead of a generic download failure.
      // Show that specific message when present, otherwise fall back to the
      // generic "please retry" toast.
      toast.error(graphDownloadErrorMessage(e))
    }
  }

  // Returns whether a request was actually dispatched to `handleSendQuestion`
  // — `false` for the early-return branches below (Deep unavailable, bare
  // "/deep"/"/plan"). Internal only: the public `sendQuestionWithCheck` below
  // (exposed as `handleSendQuestion` to every caller of this hook — the
  // composer, example clicks, follow-ups, etc.) keeps returning `Promise<void>`
  // so its widely-used signature doesn't change; only the message-queue drain
  // effect needs this richer signal, to tell "never sent" apart from "sent,
  // then errored/cancelled" (which already has its own recovery UI and must
  // NOT also be re-queued).
  const sendQuestionInternal = async ({
    question,
    isRetry,
    startResearch,
    forceWebSearch,
    agentId,
    files: filesForRequest,
  }: {
    question: string
    isRetry?: boolean
    startResearch?: boolean
    forceWebSearch?: boolean
    agentId?: string
    files?: File[]
  }): Promise<boolean> => {
    // Each new user turn starts in the pre-plan phase; clear approval flag so
    // the thinking panel captures this turn's reasoning before the plan card
    // reappears and is re-approved.
    {
      const sid = explorerStore.getState().currentSessionId
      resetDeepPlanApprovalForTurn(sid, setDeepPlanApproved)
    }

    // ── `/deep` / `/plan` prefix — Cursor/Claude Code style opt-in ──────
    // Typing "/deep <question>" or "/plan <question>" flips Deep Explorer
    // on for the session and sends the rest of the line as the question.
    // Matches Claude Code's plan-mode pill and Cursor's Shift+Tab pattern.
    // `/plan` is an alias for `/deep` — the Deep flow already produces a
    // plan.md before executing, so the keyword "plan" is a natural synonym.
    let effectiveQuestion = question
    const trimmed = question.trimStart()
    if (/^\/(deep|plan)(\b|$)/i.test(trimmed)) {
      effectiveQuestion = trimmed.replace(/^\/(deep|plan)\s*/i, "").trim()
      if (!isDeepExplorerEnabled) {
        toast.error(
          i18n.t("explorer.deep_mode_unavailable", {
            defaultValue: "Deep Explorer is disabled by your administrator.",
          })
        )
        return false
      }
      deepModeRestorePendingSessionRef.current = null
      forcedDeepSessionRef.current = null
      if (!enableDeep) {
        setEnableDeep(true)
      }
      if (effectiveQuestion.length === 0) {
        // Bare "/deep" or "/plan" just toggles — nothing to send.
        toast.success(
          i18n.t("explorer.deep_mode_enabled", {
            defaultValue: "Deep Explorer enabled for this session.",
          })
        )
        return false
      }
    }

    startResponseTimer()
    setActivityMessage(i18n.t("pages.common.answer_thinking", { defaultValue: "Thinking..." }))
    await handleSendQuestion({
      question: effectiveQuestion,
      isRetry,
      startResearch,
      forceWebSearch,
      agentId,
      files: filesForRequest,
    })
    return true
  }

  const sendQuestionWithCheck = async (
    params: Parameters<typeof sendQuestionInternal>[0]
  ): Promise<void> => {
    await sendQuestionInternal(params)
  }

  // ── Cursor-style message queue (admin-gated, #2437) ───────────────────────
  // v1 scope is plain Explorer turns only — Deep Explorer and Agent (research)
  // mode are excluded from auto-drain. Enqueueing itself is gated at the
  // composer (the page only passes onQueue when eligible); gating the drain
  // here too means a mid-session admin toggle-off stops further auto-sends.
  // (`isMessageQueueEnabled` itself now declared earlier, above `baseOnMessage`.)
  // Per session: the queue-head text last attempted via an arrival check
  // (isArrivalAtIdleSession in the drain effect below), so revisiting a
  // session whose head item already failed to dispatch doesn't repeat the
  // failed attempt — and its error toast — on every visit. A manual queue
  // edit (enqueue/remove) invalidates this: the user's action is itself a
  // reason to give the (possibly now-different) head a fresh chance next
  // time. See shouldAttemptDrain.
  const lastArrivalAttemptHeadRef = useRef<Map<string, string>>(new Map())
  // Tracks whether the drain gate was open on the previous effect run so we
  // can detect "became eligible while already idle" (flag on / Deep off, etc.).
  const prevDrainEligibleRef = useRef(false)

  const handleEnqueueMessage = useCallback(
    (question: string): boolean | void => {
      const sid = explorerStore.getState().currentSessionId
      if (!sid) return false
      // v1 queue is standard Explorer only — Deep slash commands would flip
      // `enableDeep` mid-drain and strand the rest of the queue.
      if (isDeepPlanQueuePrefix(question)) {
        toast.error(
          i18n.t("questioninput.queue_deep_not_supported", {
            defaultValue:
              "Deep Explorer messages can't be queued. Wait for the current reply to finish, then send with /deep.",
          })
        )
        return false
      }
      const queued = explorerStore.getState().sessionStates[sid]?.queuedMessages ?? []
      explorerStore.getState().updateSession(sid, {
        queuedMessages: [...queued, { id: crypto.randomUUID(), text: question }],
      })
      lastArrivalAttemptHeadRef.current.delete(sid)
    },
    [explorerStore]
  )

  const handleRemoveQueuedMessage = useCallback(
    (id: string) => {
      const sid = explorerStore.getState().currentSessionId
      if (!sid) return
      const queued = explorerStore.getState().sessionStates[sid]?.queuedMessages ?? []
      explorerStore.getState().updateSession(sid, {
        queuedMessages: queued.filter((item) => item.id !== id),
      })
      lastArrivalAttemptHeadRef.current.delete(sid)
    },
    [explorerStore]
  )

  // Drain point: fires on the busy→idle transition (isLoading / isStreaming /
  // isAwaitingSave all clear) — the same transition PR #2429 made safe for a
  // fast follow-up send — OR on landing on a different, already-idle session
  // that has a pending queue (covers a queue left behind on a session whose
  // turn settled while the user was viewing a different one: that in-session
  // transition already happened out of view, so it would otherwise never
  // fire) — OR when the drain gate newly becomes eligible while already idle
  // (Deep/Agent/flag flip). A queue on a session that's still busy in the
  // background, while the user stays on a different session throughout,
  // remains a known v1 limitation — draining it would require targeting a
  // send at a non-current session, which the send pipeline doesn't support
  // today. On a turn error, the same flags clear and `error` is set — the
  // queue is deliberately left intact either way (interaction notes, #2437):
  // the user removes items manually if the failure means they no longer apply.
  const prevBusyRef = useRef<{ sessionId: string | null; busy: boolean }>({
    sessionId: null,
    busy: false,
  })
  useEffect(() => {
    // Resolve the session from the store (not the URL-derived `currentSessionId`
    // closed over below) — during optimistic session creation the store id is
    // set before the URL navigates, so the two can transiently diverge. Reading
    // fresh here keeps this effect on the same source of truth as
    // handleEnqueueMessage/handleRemoveQueuedMessage/sendQuestionInternal.
    const sid = explorerStore.getState().currentSessionId
    const isBusy = chatState.isLoading || chatState.isStreaming || chatState.isAwaitingSave

    // Steer stop-and-redirect (#2438) owns this busy→idle settle when a
    // redirect is in flight — its own auto-send effect (use-chat.ts) reacts
    // to the same transition with no knowledge of the queue, so without this
    // guard both effects could fire a send in the same settle and start two
    // concurrent turns. Bail BEFORE touching prevBusyRef — updating it here
    // would mark the transition "consumed" while still blocked, so once
    // pendingRedirect clears the effect re-fires but sees no transition left
    // (prev and current both already idle) and never drains. Leaving the ref
    // untouched keeps it at its pre-redirect value, so the real transition is
    // detected once pendingRedirect actually clears below.
    if (chatState.pendingRedirect) {
      return
    }

    const prev = prevBusyRef.current
    const current = { sessionId: sid, busy: isBusy }
    const transitionDrain = shouldDrainQueue(prev, current)
    const freshSettle = isFreshSettle(prev, current)
    prevBusyRef.current = current

    const isDrainEligible = isMessageQueueEnabled && !enableDeep && !enableAgent
    const queued = sid ? (explorerStore.getState().sessionStates[sid]?.queuedMessages ?? []) : []
    const becameEligible = isNewlyEligibleToDrain({
      wasEligible: prevDrainEligibleRef.current,
      isEligible: isDrainEligible,
      isBusy,
      queueLength: queued.length,
    })
    prevDrainEligibleRef.current = isDrainEligible

    if ((!transitionDrain && !becameEligible) || !isDrainEligible || !sid) {
      return
    }

    if (queued.length === 0) {
      lastArrivalAttemptHeadRef.current.delete(sid)
      return
    }

    const [next, ...rest] = queued

    // Defense in depth: never auto-send /deep|/plan from the queue (would
    // enable Deep mid-drain and strand remaining items). Leave the item in
    // place; toast once per distinct head until the user edits the queue.
    if (isDeepPlanQueuePrefix(next.text)) {
      if (lastArrivalAttemptHeadRef.current.get(sid) !== next.text) {
        toast.error(
          i18n.t("questioninput.queue_deep_not_supported", {
            defaultValue:
              "Deep Explorer messages can't be queued. Wait for the current reply to finish, then send with /deep.",
          })
        )
        lastArrivalAttemptHeadRef.current.set(sid, next.text)
      }
      return
    }

    // Treat gate-flip as a fresh event so arrival-dedupe doesn't block it.
    const attemptAsFresh = freshSettle || becameEligible
    if (
      !shouldAttemptDrain({
        isFreshSettle: attemptAsFresh,
        queueHead: next.text,
        lastArrivalAttemptHead: lastArrivalAttemptHeadRef.current.get(sid),
      })
    ) {
      return
    }

    if (attemptAsFresh) {
      lastArrivalAttemptHeadRef.current.delete(sid)
    } else {
      lastArrivalAttemptHeadRef.current.set(sid, next.text)
    }

    // Drain as a normal full-turn send — no compact "Queued:" provenance on
    // the primary user bubble (that pill style is for steered markers only).
    explorerStore.getState().updateSession(sid, {
      queuedMessages: rest,
    })

    const restoreToQueueHead = () => {
      const currentQueue = explorerStore.getState().sessionStates[sid]?.queuedMessages ?? []
      explorerStore.getState().updateSession(sid, { queuedMessages: [next, ...currentQueue] })
    }
    // `dispatched === false` means sendQuestionInternal never reached
    // handleSendQuestion — restore instead of losing the text silently. A
    // message that WAS dispatched and then errored or was cancelled already
    // has its own recovery UI (error card, aborted state) and must not also
    // be duplicated back into the queue.
    // Pass `files: []` so ambient composer attachments (`filesForRequest ??
    // files` in getChatResponse) are not silently re-attached to a text-only
    // queued send.
    sendQuestionInternal({ question: next.text, files: [] })
      .then((dispatched) => {
        if (!dispatched) restoreToQueueHead()
      })
      .catch(() => restoreToQueueHead())
  }, [
    chatState.isLoading,
    chatState.isStreaming,
    chatState.isAwaitingSave,
    chatState.pendingRedirect,
    currentSessionId,
    isMessageQueueEnabled,
    enableDeep,
    enableAgent,
    explorerStore,
    sendQuestionInternal,
  ])

  // Steer markers (issue #2468 / #2469): whenever the CURRENT session's turn
  // genuinely, durably settles — isAwaitingSave's true→false transition
  // WITH sawMessageSaved true, the same "this turn is really done" signal
  // the stop-and-redirect auto-send effect (use-chat.ts) already gates on,
  // not a watchdog bailout on save failure — drain every pending
  // delivered-steer marker (there can be more than one per turn) onto the
  // now-final last message index. Queued / forced primary turns are NOT
  // provenance-tagged: they render as normal user bubbles.
  //
  // Deliberately NOT keyed on `messages.length` growth: that count is not
  // reliably monotonic here — a transient duplicate-then-corrected render
  // during the streaming→settled transition (a pre-existing quirk, not
  // introduced by this feature) can make it dip and re-grow, which
  // desyncs a simple length-delta tracker and silently drops the markers.
  // isAwaitingSave/sawMessageSaved fire exactly once, after that dust has
  // settled, so the last index read here is already the true final one.
  //
  // Only covers the session the user is actively viewing at the moment the
  // settle is observed; a background session's steer markers land unattached
  // if the user isn't on it — accepted v1 limitation.
  //
  // Tracked as a level (an "unprocessed settle is pending" flag), NOT pure
  // edge-detection on isAwaitingSave — isAwaitingSave and sawMessageSaved
  // don't always flip in the same render (message_saved can arrive on a
  // later tick than the stream settle that cleared isAwaitingSave). Naive
  // edge-detection would mark the true→false transition "consumed" on the
  // render where sawMessageSaved was still false, and then never revisit
  // it once sawMessageSaved catches up on a subsequent render.
  //
  // A first turn in a brand-new session has a further race: isAwaitingSave
  // can clear and sawMessageSaved can flip true on a render where this
  // session's `messages` array is still empty — the array populates on a
  // later tick. So `messages.length` is a dependency here too, and hitting
  // that empty window does NOT clear steerMarkersUnprocessedRef — it stays
  // armed (queue left untouched) so the render where messages actually
  // lands retries the same consumption instead of silently dropping it.
  const steerMarkersUnprocessedRef = useRef(false)
  useEffect(() => {
    const sid = explorerStore.getState().currentSessionId
    if (!sid) return
    if (chatState.isAwaitingSave) {
      // A turn is (re-)entering its save window — there's a fresh settle to
      // catch once it clears, whenever that actually happens.
      steerMarkersUnprocessedRef.current = true
      return
    }
    if (!steerMarkersUnprocessedRef.current) return
    if (!chatState.sawMessageSaved) return
    const sessionState = explorerStore.getState().sessionStates[sid]
    if (!sessionState) return
    const hasSteerMarkers = sessionState.pendingSteerMarkerQueue.length > 0
    if (!hasSteerMarkers) {
      steerMarkersUnprocessedRef.current = false
      return
    }
    const currentLength = sessionState.messages.length
    if (currentLength === 0) return
    steerMarkersUnprocessedRef.current = false
    const newIndex = currentLength - 1
    explorerStore.getState().updateSession(sid, {
      pendingSteerMarkerQueue: [],
      messageSteerMarkers: {
        ...sessionState.messageSteerMarkers,
        [newIndex]: [
          ...(sessionState.messageSteerMarkers[newIndex] ?? []),
          ...sessionState.pendingSteerMarkerQueue,
        ],
      },
    })
  }, [
    chatState.isAwaitingSave,
    chatState.sawMessageSaved,
    chatState.messages.length,
    explorerStore,
  ])

  // React Query: mutation to remove indexed file and invalidate list
  const removeIndexedFileMutation = useRemoveIndexedFileMutation()
  const [removingFileName, setRemovingFileName] = useState<string | null>(null)

  const handleRemoveIndexedFile = async (fileName: string) => {
    const session = chatService.getOpenSession(moduleSessionType)
    if (!session?.id) return
    const sessionId = session.id
    const sessionHistoryKey = queryKeys.sessionHistory(sessionType, projectId)
    let removalBatch: SessionFileRemovalBatch | undefined
    let isSessionFile = false
    let sessionRemovalSucceeded = false

    try {
      setRemovingFileName(fileName)

      // Check if this is a session file ref (Responses API file)
      const currentStoreState = explorerStore.getState()
      const cachedSession = queryClient
        .getQueryData<ChatSessionModel[]>(sessionHistoryKey)
        ?.find((candidate) => candidate?.id === sessionId)
      isSessionFile =
        currentStoreState.sessionFileRefs.includes(fileName) ||
        (cachedSession?.file_refs ?? []).includes(fileName)

      if (isSessionFile) {
        removalBatch = pendingSessionFileRemovalsRef.current.get(sessionId)
        if (!removalBatch) {
          removalBatch = {
            pending: new Set(),
            cacheOrder: cachedSession?.file_refs ?? [],
            filesInIndexOrder: currentStoreState.filesInIndex,
            sessionFileRefsOrder: currentStoreState.sessionFileRefs,
            hasSuccessfulRemoval: false,
          }
          pendingSessionFileRemovalsRef.current.set(sessionId, removalBatch)
        }
        if (removalBatch.pending.has(fileName)) {
          removalBatch = undefined
          return
        }
        removalBatch.pending.add(fileName)
      }

      // Optimistically update UI
      currentStoreState.setFilesInIndex(
        currentStoreState.filesInIndex.filter((file) => file !== fileName)
      )
      currentStoreState.setSessionFileRefs(
        currentStoreState.sessionFileRefs.filter((file) => file !== fileName)
      )

      if (isSessionFile) {
        queryClient.setQueryData<ChatSessionModel[]>(sessionHistoryKey, (sessions) =>
          sessions?.map((cachedSession) =>
            cachedSession?.id === sessionId
              ? {
                  ...cachedSession,
                  file_refs: (cachedSession.file_refs ?? []).filter((file) => file !== fileName),
                }
              : cachedSession
          )
        )
        // Remove from session file_refs in Cosmos DB (Responses API files)
        await removeSessionFileApi(sessionId, fileName, settingsService.token)
        sessionRemovalSucceeded = true
      } else {
        // Remove from Azure AI Search index (legacy indexed files)
        await removeIndexedFileMutation.mutateAsync({
          sessionId,
          fileName,
          token: settingsService.token,
        })
        // If this chat belongs to a project, also attempt removal from the project aggregator
        if (projectId) {
          await removeIndexedFileMutation.mutateAsync({
            sessionId: `project-${projectId}`,
            fileName,
            token: settingsService.token,
          })
        }
      }
    } catch (e) {
      logger.error("Failed to remove file", e as Error)
      if (isSessionFile && removalBatch) {
        const failedRemovalBatch = removalBatch
        queryClient.setQueryData<ChatSessionModel[]>(sessionHistoryKey, (sessions) =>
          sessions?.map((cachedSession) =>
            cachedSession?.id === sessionId
              ? {
                  ...cachedSession,
                  file_refs: restoreFileInOriginalOrder(
                    cachedSession.file_refs ?? [],
                    fileName,
                    failedRemovalBatch.cacheOrder
                  ),
                }
              : cachedSession
          )
        )

        // The namespace-level arrays represent only the currently open
        // session. A failed request for a session the user already left must
        // not restore that file into the new foreground session.
        if (chatService.getOpenSession(moduleSessionType)?.id === sessionId) {
          const currentStoreState = explorerStore.getState()
          currentStoreState.setSessionFileRefs(
            restoreFileInOriginalOrder(
              currentStoreState.sessionFileRefs,
              fileName,
              failedRemovalBatch.sessionFileRefsOrder
            )
          )
          currentStoreState.setFilesInIndex(
            restoreFileInOriginalOrder(
              currentStoreState.filesInIndex,
              fileName,
              failedRemovalBatch.filesInIndexOrder
            )
          )
        }
      } else {
        // Ensure the legacy Azure AI Search list syncs with server state if
        // that separate mutation path failed.
        void indexedFilesQuery.refetch()
      }
    } finally {
      if (isSessionFile && removalBatch) {
        removalBatch.pending.delete(fileName)
        removalBatch.hasSuccessfulRemoval ||= sessionRemovalSucceeded
        if (removalBatch.pending.size === 0) {
          pendingSessionFileRemovalsRef.current.delete(sessionId)
          if (removalBatch.hasSuccessfulRemoval) {
            // Refetch once the whole concurrent batch settles. Refetching
            // after the first success could resurrect another file whose
            // delete is still in flight.
            await queryClient.invalidateQueries({ queryKey: sessionHistoryKey })
          }
        }
      }
      setRemovingFileName((current) => (current === fileName ? null : current))
    }
  }

  const loadingMessage = activityMessage

  // ── Run-broadcaster reattach ─────────────────────────────────────────────
  // When a run is in flight on a session the user just opened (or that
  // survived a network blip), open a "reattach" WS to the broadcaster
  // and let frames flow into the existing onMessage path. The backend
  // route detects ``reattach: true`` and subscribes to the in-flight
  // task's frame stream instead of starting a new run; ``last_seen_seq``
  // gives the user a clean replay of frames they missed without
  // duplicating ones they already have.
  //
  // Polling fallback (use-deep-live-poll) keeps working on top of this.
  // The reattach WS is the lower-latency path; polling covers the gap
  // when the backend in-memory broadcaster is empty (process restart,
  // multi-instance deployment).
  const reattachAbortRef = useRef<AbortController | null>(null)
  const reattachInFlightRef = useRef<{
    requestGenerationId: string
    sessionId: string
  } | null>(null)

  const reattachToActiveRun = useCallback(
    (targetSessionId: string, requestedGenerationId?: string) => {
      if (!targetSessionId) return
      const requestGenerationId =
        requestedGenerationId ?? getChatRequestGenerationId(explorerStore, targetSessionId)
      if (
        !requestGenerationId ||
        !adoptChatRequestGeneration(explorerStore, targetSessionId, requestGenerationId)
      )
        return
      const activeReattach = reattachInFlightRef.current
      if (
        activeReattach?.sessionId === targetSessionId &&
        activeReattach.requestGenerationId === requestGenerationId
      )
        return
      // Don't reattach while the chat hook is itself streaming a fresh
      // request — that already owns the WS.
      if (chatState.isStreaming) return

      reattachInFlightRef.current = {
        requestGenerationId,
        sessionId: targetSessionId,
      }
      // Abort any previous reattach controller before overwriting the ref.
      // Without this the earlier socket leaks: reattachAbortRef no longer
      // references it, so the session-change cleanup can't abort it, and it
      // keeps its broadcaster subscription — every frame handled twice.
      reattachAbortRef.current?.abort()
      const abort = new AbortController()
      reattachAbortRef.current = abort
      const disposalGeneration = getChatRequestSessionDisposalGeneration(
        explorerStore,
        targetSessionId
      )

      const lastSeenCursor = lastSeenSeqRef.current[targetSessionId]
      const lastSeenSeq =
        lastSeenCursor?.requestGenerationId === requestGenerationId ? lastSeenCursor.seq : undefined
      const request = {
        reattach: true,
        request_generation_id: requestGenerationId,
        last_seen_seq: typeof lastSeenSeq === "number" ? lastSeenSeq : undefined,
        messages: [],
        stream: true,
        context: {
          overrides: {
            request_generation_id: requestGenerationId,
            session_id: targetSessionId,
          },
        },
      } as unknown as ChatAppRequest

      // Capture this socket's send fn for the identity-guarded delete below.
      let registeredSend: ((message: Record<string, unknown>) => void) | null = null

      void explorerDeepChatWebSocketApi(
        request,
        undefined,
        settingsService.token,
        abort.signal,
        (message) => {
          onMessage(message, {
            sessionId: targetSessionId,
            isActive: chatService.getOpenSession(moduleSessionType)?.id === targetSessionId,
            requestGenerationId,
            disposalGeneration,
          })
        },
        // Register the reattach WS's send fn so plan/ask_user/artifact decisions
        // dispatched after reattach (e.g. user reloads mid-plan-approval, then
        // clicks Approve) actually have a live socket to write through. Without
        // this, getDeepWsSend(targetSid) returns null and sendPlanDecision logs
        // "no active deep WebSocket connection" + shows the connection toast.
        (send) => {
          registeredSend = send
          registerDeepSocketSender(targetSessionId, send)
        },
        () => {
          // Identity-guarded delete: only clear the registry entry if it still
          // points at THIS socket — a newer foreground turn / reattach may have
          // re-registered under the same sessionId while this one was open.
          if (registeredSend) unregisterDeepSocketSender(targetSessionId, registeredSend)
          // Release the in-flight guard here — when the live socket actually
          // closes — NOT at creation. explorerDeepChatWebSocketApi resolves
          // synchronously via createStreamingResponse() (websocket.ts:213), so
          // clearing in `.finally()` freed the guard microseconds after the WS
          // opened, letting a second concurrent reattach for the same session
          // (page effect + hydration's broadcaster_active branch) subscribe to
          // the same broadcaster and double every frame.
          if (
            reattachInFlightRef.current?.sessionId === targetSessionId &&
            reattachInFlightRef.current.requestGenerationId === requestGenerationId
          ) {
            reattachInFlightRef.current = null
          }
        }
      ).catch((err) => {
        // Aborts are normal (user navigated away or run completed);
        // anything else is a real failure but the polling fallback
        // covers it, so log + swallow.
        if (abort.signal.aborted) return
        logger.warn("Deep reattach WS failed", err as Error)
        // A synchronous / connect-time rejection means onConnectionClosed
        // never fired, so release the in-flight guard here too — otherwise
        // the session stays wedged and can never reattach again.
        if (
          reattachInFlightRef.current?.sessionId === targetSessionId &&
          reattachInFlightRef.current.requestGenerationId === requestGenerationId
        ) {
          reattachInFlightRef.current = null
        }
      })
    },
    [chatService, chatState.isStreaming, explorerStore, moduleRouteId, onMessage, settingsService]
  )

  // Abort any in-flight reattach when the session changes.
  useEffect(() => {
    return () => {
      reattachAbortRef.current?.abort()
      reattachAbortRef.current = null
      reattachInFlightRef.current = null
    }
  }, [currentSessionId])

  return {
    isLoading,
    isStreaming: chatState.isStreaming,
    exampleQuestions: getExampleQuestions(moduleRoute.example_questions),
    handleExampleClicked,
    files,
    selectedDocuments,
    filesInIndex,
    sessionFileRefs, // Session file refs (from Responses API) - always show even in projects
    handleAddFiles,
    handleResetFiles,
    handleRemoveFile,
    handleGraphFileSelected,
    handleDocumentsSelected,
    handleRemoveDocument,
    handleSendQuestion: sendQuestionWithCheck,
    chatMessageStreamEnd,
    aborted,
    chatState,
    currentSessionId,
    settings,
    defaultSearchServiceId,
    sendChatRequest,
    moduleName: moduleRoute.name,
    handleStartNewChat,
    handleSharedLeaveRedirect,
    handleWebSearchChange,
    settingsService,
    chatContainerRef,
    handleCancelRequest,
    requestForceRedirect,
    enableWebSearch,
    enableTreeRetrieval,
    handleTreeRetrievalChange,
    isTreeRetrievalFeatureEnabled,
    enableM365,
    handleM365Change,
    enableServiceDesk,
    handleServiceDeskChange,
    enableNetbox,
    handleNetboxChange,
    enableXOne,
    handleXOneChange,
    enablePlugins,
    handlePluginsChange,
    disabledPluginIds,
    handlePluginDisabledChange,
    handleInstalledPluginsChange,
    enableAgent,
    handleAgentChange,
    enableDeep,
    handleDeepChange,
    openSessionInDeepMode,
    sendPlanDecision,
    sendArtifactDecision,
    sendAskUserResponse,
    skipAskUser,
    rewindToPlan,
    sendUserNote,
    totalTokenCount,
    changeTokenCounts,
    loadingMessage,
    sessionType,
    handleContinue,
    reasoningMode,
    handleReasoningModeChange,
    modelOverride,
    setModelOverride,
    effortOverride,
    setEffortOverride,
    effectiveModel,
    selectableModels,
    enableHighReasoning,
    handleHighReasoningChange,
    showImageReasoningWarning,
    handleImageReasoningWarningClose,
    handleImageReasoningWarningCancel,
    isReasoningModelActive,
    hasError,
    errorMessage,
    hasLatestPlan,
    handleStartResearch,
    showAgentModeInfo,
    setShowAgentModeInfo,
    showM365Info,
    setShowM365Info,
    hasM365Approval,
    hasDeclinedM365,
    isM365ApprovalLoading,
    handleM365Approve,
    handleM365Decline,
    handleM365Dismiss,
    handleRemoveIndexedFile,
    removingFileName, // Filename currently being removed (for loading spinner)
    markNextMessageAsPromptTemplate: () => setUsePromptTemplateForNextMessage(true),
    ensureSession,
    reattachToActiveRun,
    isMessageQueueEnabled,
    handleEnqueueMessage,
    handleRemoveQueuedMessage,
  }
}
